/**
 * Planning → Overzicht per project — de vervanger van de Airtable-interface
 * met dezelfde naam: alle taken per project, gegroepeerd per deliverable,
 * met prio, uren, deadlines en type.
 *
 * - Taken laden per project bij openklappen (43k+ records totaal).
 * - Kolommen zijn vást (px) in een eigen scroll-container: geen verspringen
 *   tussen projecten of bij het laden (feedback 26 aug).
 * - Inline bewerken zoals in Airtable: naam, prio, datums, uren en deadline
 *   schrijven via de bestaande PATCH /Api/tasks/:id (vlag-flip inbegrepen).
 * - Deliverables zijn per stuk en collectief in/uit te klappen.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DataSheetGrid, keyColumn, textColumn, floatColumn, isoDateColumn } from 'react-datasheet-grid';
import 'react-datasheet-grid/dist/style.css';
import {
  Alert, Autocomplete, Box, Button, Checkbox, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, FormControlLabel,
  InputAdornment, MenuItem, Select, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  OpenInNew as OpenInNewIcon,
  ContentCopy as ContentCopyIcon,
  DeleteOutline as DeleteOutlineIcon,
  Search as SearchIcon,
  UnfoldLess as UnfoldLessIcon,
  UnfoldMore as UnfoldMoreIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import { bevestig } from '../../utils/bevestig';
import { biedOngedaanAan } from '../../utils/ongedaan';
import { BACKEND_URL, AI_ENGINE_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { leesJson, meldFout } from '../../utils/apiFout';
import PageHeader from '../../ui-components/PageHeader';
import WijzigingslogKnop from '../../ui-components/WijzigingslogKnop';
import { PRIO_COLORS, TYPE_COLORS } from '../../utils/taakKleuren';
import { StoplichtRij, fetchProjectStatussen } from '../../ui-components/ProjectStoplicht';
import { buildGrippOfferUrl, buildGrippProjectUrl, openGrippGecontroleerd } from '../../utils/grippDeepLink';
import { useMsal } from '@azure/msal-react';
import PlanningEmailDialog from '../ProjectOverview/parts/PlanningEmailDialog';
import DeliverableDetailDialog from '../ProjectOverview/parts/DeliverableDetailDialog';
import { AFGEROND_SET } from '../../utils/taakStatus';
import { taakGewijzigd } from '../../utils/planningBronnen';
import { useUserRole } from '../../contexts/UserRoleContext';

const FONT = '"Manrope", system-ui, sans-serif';

const euDatum = (iso) => {
  if (!iso) return '';
  const [j, m, d] = String(iso).slice(0, 10).split('-');
  return j && m && d ? `${Number(d)}/${Number(m)}/${j}` : '';
};

const veldStr = (v) => (Array.isArray(v) ? v.filter(Boolean).join(', ') : String(v ?? ''));

// Minimale tabelbreedte: op smalle schermen scrolt het grid binnen zijn eigen
// container, de rest van de pagina beweegt niet mee.
// Minimale tabelbreedte volgt de kolombreedtes (som + marge), anders krijgt elk grid een eigen scrollbar en trilt de layout.
const ACTIE_BREEDTE = 60; // vaste kolom met Dupliceren / Verwijderen per regel
const tabelMinBreedte = (breedtes) => Object.values(breedtes).reduce((a, b) => a + b, 0) + 40 + ACTIE_BREEDTE;

const PRIO_OPTIES = [
  'Prio 1', 'Prio 2', 'Prio 3', 'Prio 4', 'Geen prio', 'Deadline',
  'Wacht op feedback', 'Nog niet van toepassing', 'Afgerond', 'Opgestuurd',
];

function KleurChip({ tekst, kleur }) {
  if (!tekst) return null;
  return (
    <Chip label={tekst} size="small" sx={{
      bgcolor: `${kleur || '#94a3b8'}22`, color: kleur || '#475569',
      fontWeight: 700, fontSize: 11, height: 20, fontFamily: FONT,
    }} />
  );
}

/**
 * Inline bewerkbare cel: klik → invoerveld, Enter/blur → opslaan, Esc →
 * annuleren. `type` is 'text' | 'date' | 'number'.
 */
// ── Deliverable-planning verschuiven/kopiëren (Joris, 1 sep) ────────────────
// Pure datum-arithmetiek in UTC zodat CEST geen dag verschuift.
const plusDagen = (iso, n) => {
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
};
// Weekend overslaan: vooruit → maandag, achteruit → vrijdag ("daaroverheen tillen").
const naarWerkdag = (iso, richting) => {
  const dag = new Date(`${iso}T00:00:00Z`).getUTCDay();
  if (dag === 6) return plusDagen(iso, richting < 0 ? -1 : 2);
  if (dag === 0) return plusDagen(iso, richting < 0 ? -2 : 1);
  return iso;
};
const schuifDatum = (iso, delta) => (iso ? naarWerkdag(plusDagen(iso, delta), delta) : iso);
const isAfgerond = (t) => t.afgerond || AFGEROND_SET.has(t.prio);
// Taaknaam-kern voor het matchen tussen deliverables: "Montage v1 - 9402-02. …" → "montage v1".
const taakKern = (naam) => String(naam || '').split(' - ')[0].trim().toLowerCase();

const TAAK_TYPES = ['Preproductie', 'Script', 'Regie', 'Illustratie', 'Draaidag',
  'Montage', 'Animatie', 'Grading', 'Audio', 'Overig'];
const DEADLINE_TYPES = ['Hard', 'Klant', 'Zacht', 'Intern'];

/** "Nieuwe taak" zoals het oude Airtable-formulier — per project of deliverable. */
function NieuweTaakDialog({ info, onClose, onKlaar }) {
  const [f, setF] = useState(() => ({
    naam: '', type: 'Montage', uren: '', opmerking: '', prio: 'Geen prio',
    assigneeId: '', start: '', eind: '', deadline: '', typeDeadline: '',
  }));
  const [personen, setPersonen] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { laadPickerPersonen().then(setPersonen).catch((e) => { meldFout(e, 'Personen laden'); setPersonen([]); }); }, []);
  const zet = (k) => (e) => setF((prev) => ({ ...prev, [k]: e.target.value }));

  const bewaar = async () => {
    if (!f.naam.trim()) return;
    setBusy(true);
    try {
      const payload = {
        NaamTaak: f.naam.trim(), Type: f.type, PrioNr: f.prio,
        ...(f.uren ? { UrenWerk: Number(f.uren) } : {}),
        ...(f.opmerking.trim() ? { Opmerking: f.opmerking.trim() } : {}),
        ...(f.assigneeId ? { Assignee: [f.assigneeId] } : {}),
        ...(info.projectId ? { Project: [info.projectId] } : {}),
        ...(info.deliverableId ? { Deliverable: [info.deliverableId] } : {}),
        ...(f.start ? { StartDateUitvoerder: f.start } : {}),
        ...(f.eind || f.start ? { EndDateUitvoerder: f.eind || f.start } : {}),
        ...(f.deadline ? { Deadline: f.deadline } : {}),
        ...(f.typeDeadline ? { TypeDeadline: f.typeDeadline } : {}),
      };
      const r = await authFetch(`${BACKEND_URL}/Api/tasks`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const dta = await r.json();
      if (!r.ok) throw new Error(dta.error || `HTTP ${r.status}`);
      toast.success('Taak toegevoegd');
      onKlaar();
    } catch (e) {
      toast.error(`Taak toevoegen mislukt: ${e.message}`);
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={() => !busy && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 800, fontFamily: FONT }}>
        ➕ Nieuwe taak — {info.titel}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5} sx={{ mt: 0.5 }}>
          <Stack direction="row" spacing={1}>
            <TextField size="small" autoFocus label="Taaknaam *" value={f.naam} onChange={zet('naam')} sx={{ flex: 2 }} />
            <TextField size="small" select label="Type *" value={f.type} onChange={zet('type')} sx={{ flex: 1 }}>
              {TAAK_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
            <TextField size="small" type="number" label="Uren" value={f.uren} onChange={zet('uren')} sx={{ width: 90 }} />
          </Stack>
          <TextField size="small" multiline minRows={2} label="Opmerking" value={f.opmerking} onChange={zet('opmerking')} />
          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b' }}>Prio</Typography>
            {PRIO_OPTIES.map((pr) => (
              <Chip key={pr} size="small" label={pr === 'Nog niet van toepassing' ? 'Nvt' : pr} clickable
                onClick={() => setF((prev) => ({ ...prev, prio: pr }))}
                color={f.prio === pr ? 'primary' : 'default'}
                variant={f.prio === pr ? 'filled' : 'outlined'} sx={{ height: 22, fontSize: 11 }} />
            ))}
          </Stack>
          <Autocomplete size="small" options={personen || []} loading={personen === null}
            getOptionLabel={(o) => o?.naam || ''}
            onChange={(_e, keuze) => setF((prev) => ({ ...prev, assigneeId: keuze?.id || '' }))}
            renderInput={(params) => <TextField {...params} label="Assignee — wie voert uit?" />} />
          <Stack direction="row" spacing={1}>
            <TextField size="small" type="date" label="Startdatum" InputLabelProps={{ shrink: true }} value={f.start} onChange={zet('start')} sx={{ flex: 1 }} />
            <TextField size="small" type="date" label="Einddatum" InputLabelProps={{ shrink: true }} value={f.eind} onChange={zet('eind')} sx={{ flex: 1 }} />
            <TextField size="small" type="date" label="Deadline" InputLabelProps={{ shrink: true }} value={f.deadline} onChange={zet('deadline')} sx={{ flex: 1 }} />
          </Stack>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b' }}>Type deadline</Typography>
            {DEADLINE_TYPES.map((t) => (
              <Chip key={t} size="small" label={t} clickable
                onClick={() => setF((prev) => ({ ...prev, typeDeadline: prev.typeDeadline === t ? '' : t }))}
                color={f.typeDeadline === t ? 'primary' : 'default'}
                variant={f.typeDeadline === t ? 'filled' : 'outlined'} sx={{ height: 22, fontSize: 11 }} />
            ))}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy} sx={{ textTransform: 'none' }}>Annuleren</Button>
        <Button variant="contained" onClick={bewaar} disabled={busy || !f.naam.trim()}
          sx={{ textTransform: 'none', fontWeight: 700 }}>
          {busy ? 'Bezig…' : 'Toevoegen'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function PlanningDialog({ info, onClose, onToepassen, onMail, onHerlaad }) {
  const [modus, setModus] = useState('dagen');   // 'dagen' | 'datum' | 'kopieer' | 'pauze'
  const [dagen, setDagen] = useState(3);
  const [startOp, setStartOp] = useState('');
  const [bronId, setBronId] = useState('');
  const [busy, setBusy] = useState(false);
  // ⏸ Uitgestelde projecten (9551): datums wissen maar de relatieve werkdag-
  // afstanden onthouden; later hervatten vanaf een nieuwe startdatum.
  const [pauzeStatus, setPauzeStatus] = useState(null);
  const [hervatStart, setHervatStart] = useState('');
  useEffect(() => {
    if (!info?.deliverableId) { setPauzeStatus(null); return; }
    (async () => {
      try {
        const r = await authFetch(`${AI_ENGINE_URL}/api/v1/planning/deliverable-pauze/${info.deliverableId}`);
        setPauzeStatus(await leesJson(r, 'Pauzestatus laden'));
      } catch (e) { meldFout(e, 'Pauzestatus laden'); setPauzeStatus(null); }
    })();
  }, [info?.deliverableId]);

  const pauzeer = async () => {
    if (!(await bevestig({ titel: 'Alle datums van deze deliverable wissen?', tekst: 'De onderlinge werkdag-afstanden worden onthouden zodat je later opnieuw kunt inplannen.', knop: 'Wissen', gevaarlijk: true }))) return;
    setBusy(true);
    try {
      const r = await authFetch(`${AI_ENGINE_URL}/api/v1/planning/deliverable-pauze`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ deliverable_id: info.deliverableId }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || d.error || `HTTP ${r.status}`);
      toast.success(`Planning gepauzeerd (${d.aantal_taken} taken) — volgorde onthouden`);
      onHerlaad?.(); onClose();
    } catch (e) { toast.error(`Pauzeren mislukt: ${e.message}`); }
    finally { setBusy(false); }
  };

  const hervat = async () => {
    if (!hervatStart) return;
    setBusy(true);
    try {
      const r = await authFetch(`${AI_ENGINE_URL}/api/v1/planning/deliverable-hervat`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ deliverable_id: info.deliverableId, start: hervatStart }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || d.error || `HTTP ${r.status}`);
      toast.success(`Opnieuw ingepland vanaf ${euDatum(d.vanaf)} (${(d.taken || []).length} taken)`);
      onHerlaad?.(); onClose();
    } catch (e) { toast.error(`Hervatten mislukt: ${e.message}`); }
    finally { setBusy(false); }
  };

  const actief = (info?.taken || []).filter((t) => !isAfgerond(t));
  const vroegste = actief.map((t) => t.start && String(t.start).slice(0, 10)).filter(Boolean).sort()[0] || '';

  // Wijzigingen berekenen: [{taak, velden:{...}, lokaal:{...}}]
  const wijzigingen = useMemo(() => {
    if (modus === 'kopieer') {
      const bron = (info?.andereGroepen || []).find((g) => g.id === bronId);
      if (!bron) return [];
      const perKern = new Map();
      bron.taken.forEach((t) => { if (!perKern.has(taakKern(t.naam))) perKern.set(taakKern(t.naam), t); });
      return actief.map((t) => {
        const b = perKern.get(taakKern(t.naam));
        if (!b) return null;
        const velden = {}; const lokaal = {};
        if (b.start) { velden.StartDateUitvoerder = String(b.start).slice(0, 10); lokaal.start = velden.StartDateUitvoerder; }
        if (b.eind) { velden.EndDateUitvoerder = String(b.eind).slice(0, 10); lokaal.eind = velden.EndDateUitvoerder; }
        if (b.deadline) { velden.Deadline = String(b.deadline).slice(0, 10); lokaal.deadline = velden.Deadline; }
        return Object.keys(velden).length ? { taak: t, velden, lokaal } : null;
      }).filter(Boolean);
    }
    let delta = Number(dagen) || 0;
    if (modus === 'datum') {
      if (!startOp || !vroegste) return [];
      delta = Math.round((Date.parse(`${startOp}T00:00:00Z`) - Date.parse(`${vroegste}T00:00:00Z`)) / 86400000);
    }
    if (!delta) return [];
    return actief.map((t) => {
      const velden = {}; const lokaal = {};
      if (t.start) { velden.StartDateUitvoerder = schuifDatum(String(t.start).slice(0, 10), delta); lokaal.start = velden.StartDateUitvoerder; }
      if (t.eind) { velden.EndDateUitvoerder = schuifDatum(String(t.eind).slice(0, 10), delta); lokaal.eind = velden.EndDateUitvoerder; }
      if (t.deadline) { velden.Deadline = schuifDatum(String(t.deadline).slice(0, 10), delta); lokaal.deadline = velden.Deadline; }
      return Object.keys(velden).length ? { taak: t, velden, lokaal } : null;
    }).filter(Boolean);
  }, [modus, dagen, startOp, bronId, info, actief, vroegste]);

  const pas = async () => {
    setBusy(true);
    try { await onToepassen(wijzigingen); onClose(); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!info} onClose={() => !busy && onClose()} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 800, fontFamily: FONT }}>📅 Planning — {info?.titel}</DialogTitle>
      <DialogContent dividers>
        <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          <Chip label="Verschuif met dagen" clickable color={modus === 'dagen' ? 'primary' : 'default'} onClick={() => setModus('dagen')} />
          <Chip label="Start op datum" clickable color={modus === 'datum' ? 'primary' : 'default'} onClick={() => setModus('datum')} />
          {(info?.andereGroepen || []).length > 0 && (
            <Chip label="Kopieer planning van…" clickable color={modus === 'kopieer' ? 'primary' : 'default'} onClick={() => setModus('kopieer')} />
          )}
          {info?.deliverableId && (
            <Chip label={pauzeStatus?.gepauzeerd ? '▶ Opnieuw inplannen' : '⏸ Uitstellen'} clickable
              color={modus === 'pauze' ? 'primary' : 'default'} onClick={() => setModus('pauze')} />
          )}
          <Box sx={{ flex: 1 }} />
          {onMail && (
            <Chip label="📧 Mail planning naar klant" clickable onClick={() => { onClose(); onMail(); }} />
          )}
        </Stack>

        {modus === 'dagen' && (
          <TextField size="small" type="number" label="Aantal dagen (negatief = vervroegen)"
            value={dagen} onChange={(e) => setDagen(e.target.value)} sx={{ width: 260 }} />
        )}
        {modus === 'datum' && (
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField size="small" type="date" label="Eerste taak start op" InputLabelProps={{ shrink: true }}
              value={startOp} onChange={(e) => setStartOp(e.target.value)} />
            {vroegste && <Typography variant="caption" color="text.secondary">nu: {euDatum(vroegste)}</Typography>}
          </Stack>
        )}
        {modus === 'pauze' && (
          pauzeStatus?.gepauzeerd ? (
            <Stack spacing={1.5}>
              <Alert severity="info">
                Gepauzeerd sinds {euDatum(pauzeStatus.sinds)} — {pauzeStatus.aantal_taken} taken met
                onthouden werkdag-afstanden. Kies de nieuwe startdatum; weekenden schuiven vanzelf door.
              </Alert>
              <Stack direction="row" spacing={2} alignItems="center">
                <TextField size="small" type="date" label="Eerste taak start op" InputLabelProps={{ shrink: true }}
                  value={hervatStart} onChange={(e) => setHervatStart(e.target.value)} />
                <Button variant="contained" disabled={!hervatStart || busy} onClick={hervat}
                  sx={{ textTransform: 'none', fontWeight: 700 }}>▶ Plan opnieuw in</Button>
              </Stack>
            </Stack>
          ) : (
            <Stack spacing={1.5}>
              <Alert severity="warning">
                Project uitgesteld? Dit wist alle start-/eind-/deadline-datums van deze deliverable,
                maar onthoudt de onderlinge werkdag-afstanden. Zodra de nieuwe datum bekend is plan je
                alles met één klik opnieuw in — met dezelfde logica en zonder weekend-datums.
              </Alert>
              <Box>
                <Button variant="outlined" color="warning" disabled={busy} onClick={pauzeer}
                  sx={{ textTransform: 'none', fontWeight: 700 }}>⏸ Wis datums, onthoud volgorde</Button>
              </Box>
            </Stack>
          )
        )}
        {modus === 'kopieer' && (
          <Select size="small" displayEmpty value={bronId} onChange={(e) => setBronId(e.target.value)} sx={{ minWidth: 320 }}>
            <MenuItem value="">Kies de deliverable om van te kopiëren…</MenuItem>
            {(info?.andereGroepen || []).map((g) => (
              <MenuItem key={g.id} value={g.id}>{g.titel}</MenuItem>
            ))}
          </Select>
        )}

        {modus !== 'pauze' && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, mb: 0.5 }}>
            {modus === 'kopieer'
              ? 'Taken worden gematcht op naam (bv. “Montage v1”). Afgeronde taken blijven staan.'
              : 'Onderlinge afstanden (feedbackmomenten) blijven gelijk; datums in het weekend schuiven door naar een werkdag. Afgeronde taken blijven staan.'}
          </Typography>
        )}
        {modus === 'pauze' ? null : wijzigingen.length === 0 ? (
          <Alert severity="info" sx={{ mt: 1 }}>Nog geen wijzigingen — kies hierboven wat er moet gebeuren.</Alert>
        ) : (
          <Box sx={{ mt: 1, maxHeight: 320, overflow: 'auto', border: '1px solid #e2e8f0', borderRadius: 1.5 }}>
            {wijzigingen.map(({ taak, lokaal }) => (
              <Box key={taak.id} sx={{ display: 'flex', gap: 1.5, px: 1.5, py: 0.5, borderTop: '1px solid #f8fafc', alignItems: 'baseline' }}>
                <Typography noWrap sx={{ fontSize: 12.5, flex: 1, minWidth: 0 }}>{taak.naam}</Typography>
                <Typography sx={{ fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>
                  {euDatum(taak.start)}{taak.eind ? `–${euDatum(taak.eind)}` : ''} → <b>{euDatum(lokaal.start || taak.start)}{(lokaal.eind || taak.eind) ? `–${euDatum(lokaal.eind || taak.eind)}` : ''}</b>
                  {lokaal.deadline ? ` · deadline ${euDatum(lokaal.deadline)}` : ''}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy} sx={{ textTransform: 'none' }}>Annuleren</Button>
        {modus !== 'pauze' && (
          <Button variant="contained" onClick={pas} disabled={busy || wijzigingen.length === 0}
            sx={{ textTransform: 'none', fontWeight: 700 }}>
            {busy ? 'Bezig…' : `Pas toe op ${wijzigingen.length} taken`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

/** Prio als klikbare chip met dropdown. */
// ── Spreadsheet-grid (react-datasheet-grid, MIT) ────────────────────────────
// Lars: "Airtable was meer Excel." Een eigen grid haalde dat gevoel niet; deze
// bibliotheek doet blokselectie, vulgreep, kopiëren/plakken en toetsenbord-
// navigatie zoals Excel. Per deliverable één grid (zelfde kolommen, dus alles
// lijnt uit); opslaan blijft per gewijzigde cel via de bestaande PATCH.

// "2/10/2026", "02-10-2026", "2.10.26", "2026-10-02" → ISO. Leeg of onherkenbaar → ''.
function naarISO(t) {
  const z = String(t || '').trim();
  if (!z) return '';
  let m = z.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) return `${m[1]}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}`;
  m = z.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})$/);
  if (!m) return '';
  const jaar = m[3].length === 2 ? `20${m[3]}` : m[3];
  return `${jaar}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
}

// Personen voor de pickers: één keer laden, met rec-id + functie.
// De 'Nog in te plannen'/'Freelance'-bakken zijn Systeem-personen met de juiste
// Functie (Editor/Animator), dus functie-matching zet ze vanzelf bovenaan.
let _personenCache = null;
async function laadPickerPersonen() {
  if (_personenCache) return _personenCache;
  const r = await authFetch(`${AI_ENGINE_URL}/api/v1/airtable/persons?status=Active`);
  const d = await leesJson(r, 'Personen laden');
  _personenCache = (d.people || []).map((p) => ({
    id: p.id, naam: p.naam, functie: String(p.functie || '').toLowerCase(),
    type: p.type || '',
  })).filter((p) => p.naam && ['Medewerker', 'Stagiair', 'Systeem'].includes(p.type));
  return _personenCache;
}
// Synchroon (voor plakken): naam → persoon uit de cache; exact, dan begin, dan bevat.
function zoekPersoon(naam) {
  const z = String(naam || '').trim().toLowerCase();
  if (!z || !_personenCache) return null;
  const exact = _personenCache.find((p) => p.naam.toLowerCase() === z);
  if (exact) return exact;
  // Alleen een UNIEKE prefix-/deelmatch (Codex 2 sep: "Jan" koos stil de eerste Jan).
  const prefix = _personenCache.filter((p) => p.naam.toLowerCase().startsWith(z));
  if (prefix.length === 1) return prefix[0];
  if (prefix.length > 1) return null;
  const bevat = _personenCache.filter((p) => p.naam.toLowerCase().includes(z));
  return bevat.length === 1 ? bevat[0] : null;
}

// ── Nieuwe taken uit het grid (dupliceren met ⌘D, plakken onder de laatste regel) ──
// Joris (26 sep): "werkt lekker snel met verdubbelen van deliverables, kopiëren van een
// deliverable naar nieuwe". Een nieuwe rij krijgt een tijdelijk id tot de engine hem
// heeft aangemaakt; daarna laadt het project opnieuw.
let _nieuwTeller = 0;
const nieuwRijId = () => { _nieuwTeller += 1; return `nieuw-${Date.now()}-${_nieuwTeller}`; };
const isoOfLeeg = (v) => (v ? String(v).slice(0, 10) : '');

/**
 * Een gridrij (of bestaande taak) → payload voor POST /Api/tasks, of null zonder naam.
 * Afgerond/Opgestuurd wordt 'Geen prio' (een kopie is nog niet af). De uitvoerder gaat mee
 * op record-id; zonder id op naam, en alleen als die eenduidig is (zoekPersoon). Geen
 * uitvoerder = `zonderAssignee` (de engine vult dan niet de ingelogde gebruiker in).
 */
export function naarNieuweTaak(rij, { projectId = null, deliverableId = null } = {}, vindPersoon = zoekPersoon) {
  const naam = String(rij?.naam || '').trim();
  if (!naam) return null;
  const prio = !rij.prio || AFGEROND_SET.has(rij.prio) ? 'Geen prio' : rij.prio;
  const assigneeId = rij.assigneeId || vindPersoon(String(rij.assignee || '').split(',')[0])?.id || null;
  const start = isoOfLeeg(rij.start);
  const eind = isoOfLeeg(rij.eind) || start;
  return {
    NaamTaak: naam, PrioNr: prio,
    ...(rij.type ? { Type: rij.type } : {}),
    ...(typeof rij.uren === 'number' && Number.isFinite(rij.uren) ? { UrenWerk: rij.uren } : {}),
    ...(start ? { StartDateUitvoerder: start } : {}),
    ...(eind ? { EndDateUitvoerder: eind } : {}),
    ...(rij.deadline ? { Deadline: isoOfLeeg(rij.deadline) } : {}),
    ...(rij.typeDeadline ? { TypeDeadline: rij.typeDeadline } : {}),
    ...(rij.opmerking ? { Opmerking: rij.opmerking } : {}),
    // Zonder uitvoerder blijft de taak eigenaarloos (anders vult de engine de maker in).
    ...(assigneeId ? { Assignee: [assigneeId] } : { zonderAssignee: true }),
    ...(projectId ? { Project: [projectId] } : {}),
    ...(deliverableId ? { Deliverable: [deliverableId] } : {}),
  };
}

// Welke functies passen bij welk taaktype — voor de volgorde in de picker.
const FUNCTIES_PER_TAAKTYPE = {
  Montage: ['editor'],
  Animatie: ['animator', 'illustrat'],
  Illustratie: ['illustrat', 'animator'],
  Regie: ['regisseur', 'regie', 'creative', 'creatief'],
  Grading: ['editor', 'color'],
  Audio: ['editor', 'sound'],
  Script: ['copywriter', 'regisseur', 'creative'],
};

const celTekst = { fontSize: 12.5, px: 1, lineHeight: '30px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontFamily: 'inherit' };

/** Persoonscel: tonen als tekst; bij bewerken een zoeklijst met passende personen bovenaan. */
function PersoonCel({ rowData, setRowData, focus, stopEditing, columnData }) {
  const { veld, idVeld, metFuncties } = columnData;
  const [personen, setPersonen] = useState(_personenCache);
  useEffect(() => { if (focus && !personen) laadPickerPersonen().then(setPersonen).catch((e) => { meldFout(e, 'Personen laden'); setPersonen([]); }); }, [focus, personen]);
  if (!focus) return <Box sx={{ ...celTekst, color: rowData[veld] ? '#334155' : '#cbd5e1' }}>{rowData[veld] || '—'}</Box>;
  const functies = metFuncties ? (FUNCTIES_PER_TAAKTYPE[rowData.type] || []) : [];
  const past = (p) => functies.length === 0 || functies.some((f) => p.functie.includes(f));
  const opties = (personen || [])
    .map((p) => ({ ...p, groep: past(p) ? (functies.length ? `Passend bij ${rowData.type}` : 'Personen') : 'Overige' }))
    .sort((a, b) => (a.groep === b.groep ? a.naam.localeCompare(b.naam) : a.groep === 'Overige' ? 1 : -1));
  return (
    <Autocomplete size="small" options={opties} autoHighlight openOnFocus open
      groupBy={(o) => o.groep} getOptionLabel={(o) => o.naam}
      onChange={(e, p) => { if (p) setRowData({ ...rowData, [veld]: p.naam, [idVeld]: p.id }); stopEditing({ nextRow: false }); }}
      onClose={(e, reden) => { if (reden !== 'selectOption') stopEditing({ nextRow: false }); }}
      sx={{ width: '100%' }}
      // De lijst krijgt een eigen, vaste breedte (MUI maakt 'm anders zo smal als de cel,
      // waardoor namen over drie regels braken) en zweeft boven het grid.
      componentsProps={{
        popper: { placement: 'bottom-start', sx: { width: '320px !important', zIndex: 1400 }, modifiers: [{ name: 'offset', options: { offset: [-8, 4] } }] },
        paper: { sx: { borderRadius: 2, boxShadow: '0 12px 32px rgba(15,23,42,0.16)', border: '1px solid #e2e8f0' } },
      }}
      ListboxProps={{ sx: { py: 0.5, maxHeight: 320 } }}
      renderGroup={(params) => (
        <li key={params.key}>
          <Typography sx={{ px: 1.5, pt: 1, pb: 0.25, fontSize: 10.5, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#94a3b8', fontFamily: FONT }}>{params.group}</Typography>
          <Box component="ul" sx={{ p: 0, m: 0, listStyle: 'none' }}>{params.children}</Box>
        </li>
      )}
      renderOption={(props, o) => (
        <Box component="li" {...props} key={o.id} sx={{ fontSize: 13, minHeight: '32px !important', py: '4px !important', px: '12px !important', gap: 1 }}>
          <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: '#e2e8f0', color: '#475569', fontSize: 10, fontWeight: 800, display: 'grid', placeItems: 'center', flexShrink: 0, fontFamily: FONT }}>
            {String(o.naam).replace(/^\d+\.\s*/, '').split(' ').slice(0, 2).map((w) => w[0]).join('').toUpperCase()}
          </Box>
          <Typography noWrap sx={{ fontSize: 13 }}>{o.naam}</Typography>
        </Box>
      )}
      renderInput={(params) => <TextField {...params} autoFocus placeholder="Zoek persoon…" variant="standard" InputProps={{ ...params.InputProps, disableUnderline: true, sx: { fontSize: 12.5, px: 1 } }} />} />
  );
}
function persoonKolom({ veld, idVeld, title, metFuncties = false, ...rest }) {
  return {
    title, component: PersoonCel, columnData: { veld, idVeld, metFuncties }, disableKeys: true, keepFocus: true,
    copyValue: ({ rowData }) => rowData[veld] || '',
    pasteValue: ({ rowData, value }) => { const p = zoekPersoon(value); return p ? { ...rowData, [veld]: p.naam, [idVeld]: p.id } : rowData; },
    deleteValue: ({ rowData }) => rowData,
    isCellEmpty: ({ rowData }) => !rowData[veld],
    ...rest,
  };
}

/** Keuzecel: chip tonen; bij bewerken een open keuzelijst. Plakken matcht op naam of alias. */
function KeuzeCel({ rowData, setRowData, focus, stopEditing, columnData }) {
  const { veld, opties, render } = columnData;
  if (!focus) return <Box sx={{ ...celTekst, display: 'flex', alignItems: 'center' }}>{render(rowData[veld])}</Box>;
  return (
    <Select variant="standard" disableUnderline open autoFocus value={opties.includes(rowData[veld]) ? rowData[veld] : ''}
      onChange={(e) => { setRowData({ ...rowData, [veld]: e.target.value }); stopEditing({ nextRow: false }); }}
      onClose={() => stopEditing({ nextRow: false })} displayEmpty
      renderValue={() => <Box sx={{ display: 'flex', alignItems: 'center' }}>{render(rowData[veld])}</Box>}
      sx={{ width: '100%', px: 1, '& .MuiSelect-icon': { display: 'none' } }}>
      {opties.map((o) => <MenuItem key={o} value={o} dense>{render(o)}</MenuItem>)}
    </Select>
  );
}
function keuzeKolom({ veld, title, opties, render, aliassen = {}, ...rest }) {
  return {
    title, component: KeuzeCel, columnData: { veld, opties, render }, disableKeys: true, keepFocus: true,
    copyValue: ({ rowData }) => rowData[veld] || '',
    pasteValue: ({ rowData, value }) => {
      const z = String(value || '').trim().toLowerCase();
      const prefix = opties.filter((o) => o.toLowerCase().startsWith(z));
      const hit = opties.find((o) => o.toLowerCase() === z) || aliassen[z] || (prefix.length === 1 ? prefix[0] : null);
      return hit ? { ...rowData, [veld]: hit } : rowData;
    },
    deleteValue: ({ rowData }) => rowData,
    isCellEmpty: ({ rowData }) => !rowData[veld],
    ...rest,
  };
}
// Datumkolom op ISO-strings; plakken accepteert ook 2/10/2026 en 02-10-26.
function datumKolom(veld, title, rest = {}) {
  return {
    ...keyColumn(veld, isoDateColumn), title,
    copyValue: ({ rowData }) => (rowData[veld] ? String(rowData[veld]).slice(0, 10) : ''),
    pasteValue: ({ rowData, value }) => { const iso = naarISO(value); return (String(value || '').trim() && !iso) ? rowData : { ...rowData, [veld]: iso || null }; },
    ...rest,
  };
}
const PRIO_ALIASSEN = { nvt: 'Nog niet van toepassing', p1: 'Prio 1', p2: 'Prio 2', p3: 'Prio 3', p4: 'Prio 4', geen: 'Geen prio', feedback: 'Wacht op feedback' };
const leeg = () => <Typography sx={{ fontSize: 12, color: '#cbd5e1' }}>—</Typography>;

// Kolombreedtes: sleep de rechterrand van een kolomkop. Eén module-brede store
// zodat alle grids (per deliverable, per project) dezelfde breedtes houden; de
// keuze wordt per browser onthouden.
const BREEDTE_SLEUTEL = 'overzicht-per-project:kolombreedtes:v1';
const STANDAARD_BREEDTES = { naam: 260, assignee: 120, prio: 100, start: 104, eind: 104, uren: 60, deadline: 104, typeDeadline: 80, deadlineDeliverable: 104, type: 104, submitter: 110 };
const breedteStore = {
  breedtes: (() => { try { return { ...STANDAARD_BREEDTES, ...JSON.parse(localStorage.getItem(BREEDTE_SLEUTEL) || '{}') }; } catch { return { ...STANDAARD_BREEDTES }; } })(),
  luisteraars: new Set(),
  zet(id, px) {
    this.breedtes = { ...this.breedtes, [id]: Math.max(48, Math.round(px)) };
    try { localStorage.setItem(BREEDTE_SLEUTEL, JSON.stringify(this.breedtes)); } catch { /* privémodus */ }
    this.luisteraars.forEach((f) => f(this.breedtes));
  },
  reset() { this.breedtes = { ...STANDAARD_BREEDTES }; try { localStorage.removeItem(BREEDTE_SLEUTEL); } catch { /* */ } this.luisteraars.forEach((f) => f(this.breedtes)); },
};
function useKolombreedtes() {
  const [breedtes, setBreedtes] = useState(breedteStore.breedtes);
  useEffect(() => { breedteStore.luisteraars.add(setBreedtes); return () => breedteStore.luisteraars.delete(setBreedtes); }, []);
  return breedtes;
}
function KolomKop({ id, label }) {
  const start = (e) => {
    e.preventDefault(); e.stopPropagation();
    const x0 = e.clientX; const b0 = breedteStore.breedtes[id];
    const ac = new AbortController();
    const beweeg = (ev) => breedteStore.zet(id, b0 + (ev.clientX - x0));
    const los = () => { ac.abort(); document.body.style.cursor = ''; };
    document.body.style.cursor = 'col-resize';
    // Eén AbortController ruimt alles op — ook als het venster focus verliest of het slepen afbreekt.
    document.addEventListener('mousemove', beweeg, { signal: ac.signal });
    document.addEventListener('mouseup', los, { signal: ac.signal });
    window.addEventListener('blur', los, { signal: ac.signal });
    document.addEventListener('pointercancel', los, { signal: ac.signal });
  };
  return (
    <Box sx={{ position: 'relative', width: '100%', pr: 1 }} title="Sleep de rand om de kolom breder of smaller te maken">
      <span>{label}</span>
      <Box onMouseDown={start} onDoubleClick={(e) => { e.stopPropagation(); breedteStore.zet(id, STANDAARD_BREEDTES[id]); }}
        sx={{ position: 'absolute', right: -6, top: -6, bottom: -6, width: 10, cursor: 'col-resize', '&:hover': { bgcolor: '#bfdbfe' } }} />
    </Box>
  );
}

const GRID_KOLOMMEN_BASIS = [
  { id: 'naam', ...keyColumn('naam', textColumn), title: 'Taak', grow: 1, minWidth: 48,
    deleteValue: ({ rowData }) => rowData }, // een taak zonder naam bestaat niet
  persoonKolom({ id: 'assignee', veld: 'assignee', idVeld: 'assigneeId', title: 'Assignee', metFuncties: true, grow: 0, minWidth: 48 }),
  keuzeKolom({ id: 'prio', veld: 'prio', title: 'Prio', opties: PRIO_OPTIES, aliassen: PRIO_ALIASSEN, grow: 0, minWidth: 48,
    render: (v) => (v ? <KleurChip tekst={v === 'Nog niet van toepassing' ? 'Nvt' : v} kleur={PRIO_COLORS[v]} /> : leeg()) }),
  datumKolom('start', 'Start', { id: 'start', grow: 0, minWidth: 48 }),
  datumKolom('eind', 'Eind', { id: 'eind', grow: 0, minWidth: 48 }),
  { id: 'uren', ...keyColumn('uren', floatColumn), title: 'Uren', grow: 0, minWidth: 48 },
  datumKolom('deadline', 'Deadline', { id: 'deadline', grow: 0, minWidth: 48 }),
  keuzeKolom({ id: 'typeDeadline', veld: 'typeDeadline', title: 'Type dl', opties: DEADLINE_TYPES, grow: 0, minWidth: 48,
    render: (v) => (v ? <KleurChip tekst={v} kleur={v === 'Hard' ? '#b91c1c' : '#64748b'} /> : leeg()) }),
  { id: 'deadlineDeliverable', title: 'Deadline deliv.', grow: 0, minWidth: 48, disabled: true,
    component: ({ rowData }) => <Box sx={{ ...celTekst, color: '#94a3b8' }}>{euDatum(rowData.deadlineDeliverable)}</Box>,
    copyValue: ({ rowData }) => euDatum(rowData.deadlineDeliverable), pasteValue: ({ rowData }) => rowData, deleteValue: ({ rowData }) => rowData },
  keuzeKolom({ id: 'type', veld: 'type', title: 'Type', opties: TAAK_TYPES, grow: 0, minWidth: 48,
    render: (v) => (v ? <KleurChip tekst={v} kleur={TYPE_COLORS[v]} /> : leeg()) }),
  persoonKolom({ id: 'submitter', veld: 'submitter', idVeld: 'submitterId', title: 'Submitter', grow: 0, minWidth: 48 }),
];
// Kolommen met de actuele breedtes en een sleepbare kop.
const metBreedtes = (breedtes) => GRID_KOLOMMEN_BASIS.map((k) => ({
  ...k, basis: breedtes[k.id] || STANDAARD_BREEDTES[k.id], shrink: 0,
  title: <KolomKop id={k.id} label={k.title} />,
}));

// Welke rijvelden naar welk Airtable-veld gaan bij opslaan.
const VELD_NAAR_AIRTABLE = {
  naam: (v) => ({ NaamTaak: v }), prio: (v) => ({ PrioNr: v }), start: (v) => ({ StartDateUitvoerder: v }),
  eind: (v) => ({ EndDateUitvoerder: v }), uren: (v) => ({ UrenWerk: v }), deadline: (v) => ({ Deadline: v }),
  typeDeadline: (v) => ({ TypeDeadline: v }), type: (v) => ({ Type: v }),
  assigneeId: (v) => ({ Assignee: [v] }), submitterId: (v) => ({ Submitter: [v] }),
};
const RIJ_HOOGTE = 30;

/**
 * Rij-acties (Joris, 3 nov: "hier wil je ook taken dupliceren en verwijderen"). Rustig: de
 * iconen verschijnen pas als je over de regel beweegt of er met Tab op komt (GRID_STIJL).
 * Alleen voor taken die al bestaan; een rij die nog wordt aangemaakt heeft geen acties.
 */
export function RijActies({ rowData, columnData }) {
  const { onDupliceer, onVerwijder } = columnData || {};
  if (!rowData?.id || rowData.nieuw || String(rowData.id).startsWith('nieuw-')) return null;
  const knop = (label, icoon, actie, gevaar = false) => (
    <Tooltip title={label} disableInteractive>
      <Box component="button" type="button" aria-label={`${label}: ${rowData.naam || 'taak'}`}
        // Het grid luistert zelf naar mousedown (cel selecteren); de klik is van de knop.
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => { e.stopPropagation(); actie(rowData); }}
        sx={{ border: 0, bgcolor: 'transparent', p: 0.25, borderRadius: 1, cursor: 'pointer', display: 'inline-flex',
          color: '#94a3b8', '&:hover': { color: gevaar ? '#b91c1c' : '#334155', bgcolor: gevaar ? '#fee2e2' : '#e2e8f0' },
          '&:focus-visible': { outline: '2px solid #2563eb', outlineOffset: 1 } }}>
        {icoon}
      </Box>
    </Tooltip>
  );
  return (
    <Box className="rij-acties" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.25, height: '100%', px: 0.5 }}>
      {onDupliceer && knop('Dupliceren', <ContentCopyIcon sx={{ fontSize: 15 }} />, onDupliceer)}
      {onVerwijder && knop('Verwijderen', <DeleteOutlineIcon sx={{ fontSize: 17 }} />, onVerwijder, true)}
    </Box>
  );
}
const actieKolom = (columnData) => ({
  id: 'acties', title: '', basis: ACTIE_BREEDTE, grow: 0, shrink: 0, minWidth: ACTIE_BREEDTE, disabled: true,
  component: RijActies, columnData,
  copyValue: () => '', pasteValue: ({ rowData }) => rowData, deleteValue: ({ rowData }) => rowData,
});
const kopieVan = (rowData) => ({ ...rowData, id: nieuwRijId(), nieuw: true, afgerond: false });

/** Eén grid per deliverable: wijzigingen per rij vergelijken en als PATCH doorgeven. */
function TakenGrid({ taken, onBewerk, onNieuw, onVerwijder, metKop }) {
  useEffect(() => { laadPickerPersonen().catch(() => {}); }, []); // bewust stil: voorladen voor de pickers — die melden het zelf bij openen
  const breedtes = useKolombreedtes();
  // Dupliceren via de knop = hetzelfde als ⌘D: een kopie (niet afgerond) die als nieuwe taak wordt aangemaakt.
  const kolommen = useMemo(() => [...metBreedtes(breedtes), actieKolom({
    onDupliceer: onNieuw ? (rij) => onNieuw([kopieVan(rij)]) : undefined,
    onVerwijder,
  })], [breedtes, onNieuw, onVerwijder]);
  // Toetsenbord: de iconen zijn met Tab niet bereikbaar (het grid vangt Tab zelf), dus ⌘⌫ /
  // Ctrl+Backspace verwijdert de regel van de actieve cel — met dezelfde bevestiging.
  const gridRef = useRef(null);
  const takenRef = useRef(taken);
  takenRef.current = taken;
  useEffect(() => {
    if (!onVerwijder) return undefined;
    const h = (e) => {
      if (!(e.metaKey || e.ctrlKey) || e.key !== 'Backspace') return;
      const cel = gridRef.current?.activeCell;
      if (!cel) return;
      if (document.activeElement?.classList?.contains('dsg-input')) return; // aan het typen
      const rij = takenRef.current[cel.row];
      if (!rij || rij.nieuw || String(rij.id || '').startsWith('nieuw-')) return;
      // In de capture-fase op window en de toets opeten: anders wist de eigen Backspace-handler
      // van het grid (op document) ook de geselecteerde cellen, zelfs als je daarna annuleert.
      e.preventDefault();
      e.stopPropagation();
      onVerwijder(rij);
    };
    window.addEventListener('keydown', h, true);
    return () => window.removeEventListener('keydown', h, true);
  }, [onVerwijder]);
  const onChange = (nieuw, operaties) => {
    const batch = `${Date.now()}-${Math.random()}`; // alles uit één actie = één undo-stap
    const nieuweRijen = [];
    operaties.forEach((op) => {
      // Nieuwe rijen (⌘D = dupliceren, plakken voorbij de laatste regel): aanmaken.
      // Een lege rij (geen naam) maken we niet aan; verwijderen van rijen kan hier niet.
      if (op.type === 'CREATE') {
        nieuweRijen.push(...nieuw.slice(op.fromRowIndex, op.toRowIndex).filter((r) => String(r?.naam || '').trim()));
        return;
      }
      if (op.type !== 'UPDATE') return;
      for (let i = op.fromRowIndex; i < op.toRowIndex; i += 1) {
        const oud = taken[i]; const rij = nieuw[i];
        if (!oud || !rij || oud.id !== rij.id) continue;
        const velden = {}; const lokaal = {};
        Object.entries(VELD_NAAR_AIRTABLE).forEach(([k, naar]) => {
          if ((rij[k] ?? null) === (oud[k] ?? null)) return;
          if (k === 'naam' && !String(rij.naam || '').trim()) return;
          Object.assign(velden, naar(rij[k])); lokaal[k] = rij[k];
        });
        if (lokaal.assigneeId) { lokaal.assignee = rij.assignee; lokaal.assigneeIds = [rij.assigneeId]; }
        if (lokaal.submitterId) lokaal.submitter = rij.submitter;
        if (Object.keys(velden).length) onBewerk(oud, velden, lokaal, { batch });
      }
    });
    if (nieuweRijen.length && onNieuw) onNieuw(nieuweRijen);
  };
  return (
    <DataSheetGrid ref={gridRef} value={taken} onChange={onChange} columns={kolommen} rowKey="id"
      lockRows={!onNieuw} disableSmartDelete gutterColumn={false} addRowsComponent={false} disableContextMenu
      createRow={() => ({ id: nieuwRijId(), nieuw: true })}
      duplicateRow={({ rowData }) => kopieVan(rowData)}
      rowHeight={RIJ_HOOGTE} headerRowHeight={metKop ? 28 : 0}
      height={taken.length * RIJ_HOOGTE + (metKop ? 28 : 0) + 2}
      rowClassName={({ rowData }) => (rowData.afgerond || ['Afgerond', 'Opgestuurd'].includes(rowData.prio) ? 'rij-af' : undefined)} />
  );
}
// Kleine stijlcorrecties op de bibliotheek: kleiner lettertype, geen dubbele randen.
const GRID_STIJL = [
  '.dsg-container{--dsg-border-color:#f1f5f9;--dsg-selection-border-color:#2563eb;--dsg-selection-background-color:#dbeafe66;--dsg-header-text-color:#94a3b8;--dsg-cell-background-color:transparent;border:none;font-family:inherit}',
  '.dsg-cell-header{font-size:10.5px;font-weight:800;text-transform:uppercase;letter-spacing:.4px;background:#fff}',
  '.dsg-cell{font-size:12.5px}.dsg-input{font-size:12.5px;padding:0 8px}',
  // Hoogte is exact op de rijen berekend; een verschijnende/verdwijnende scrollbar (macOS-overlay bij hover) liet de rijen trillen.
  '.dsg-container{overflow:hidden!important}.dsg-container>div{scrollbar-width:none}.dsg-container>div::-webkit-scrollbar{display:none}',
  '.dsg-row.rij-af .dsg-cell{opacity:.55}',
  // Rij-acties alleen bij hover of toetsenbordfocus: het grid blijft rustig.
  '.rij-acties{opacity:0;transition:opacity .12s}.dsg-row:hover .rij-acties,.rij-acties:focus-within{opacity:1}',
  '@media (hover:none){.rij-acties{opacity:1}}',
].join('');

// Signalen uit Planning → Nog in te plannen (deadline-check), getoond naast de deadline.
const SIGNAAL_TEKST = { geen_deadline: 'Geen deadline', voor_draaidag: 'Vóór de draaidag', verlopen: 'Verlopen', past_niet: 'Past niet' };
const SIGNAAL_KLEUR = { past_niet: { color: '#b45309', bgcolor: '#fef3c7' } };
function SignaalChips({ signalen }) {
  if (!signalen?.length) return null;
  return (
    <Stack direction="row" spacing={0.5} onClick={(e) => e.stopPropagation()}>
      {signalen.map((c) => <Chip key={c} size="small" label={SIGNAAL_TEKST[c] || c} sx={{ height: 18, fontSize: 10.5, fontWeight: 700, color: '#b91c1c', bgcolor: '#fee2e2', ...(SIGNAAL_KLEUR[c] || {}) }} />)}
    </Stack>
  );
}

function DeliverableGroep({ titel, deadline, remarks, taken, dicht, onToggle, onBewerk, onNieuw, onVerwijder, onPlanning, onNieuweTaak, onDupliceer, onDeadline, onOpen, metKop, signalen }) {
  const uren = taken.reduce((s, t) => s + (typeof t.uren === 'number' ? t.uren : 0), 0);
  // Deadline van de deliverable direct in de kop aanpasbaar (Joris, 1 sep).
  const [dlBewerk, setDlBewerk] = useState(false);
  return (
    <Box sx={{ mb: 0.5 }}>
      <Box onClick={onToggle} sx={{
        display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.6,
        bgcolor: '#f8fafc', cursor: 'pointer', '&:hover': { bgcolor: '#f1f5f9' },
        position: 'sticky', left: 0,
      }}>
        {dicht
          ? <ChevronRightIcon sx={{ fontSize: 15, color: '#94a3b8' }} />
          : <ExpandMoreIcon sx={{ fontSize: 15, color: '#94a3b8' }} />}
        <Tooltip title={onOpen ? 'Deliverable openen: deadline, formaat, lengte, briefing…' : ''}>
          <Typography onClick={onOpen ? (e) => { e.stopPropagation(); onOpen(); } : undefined}
            sx={{ fontSize: 12, fontWeight: 800, color: '#334155', fontFamily: FONT, cursor: onOpen ? 'pointer' : 'default',
              '&:hover': onOpen ? { color: '#1e3a8a', textDecoration: 'underline' } : {} }}>{titel}</Typography>
        </Tooltip>
        {onDeadline && dlBewerk ? (
          <input
            type="date"
            autoFocus
            defaultValue={String(deadline || '').slice(0, 10)}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => { if (e.key === 'Escape') setDlBewerk(false); }}
            onBlur={(e) => {
              setDlBewerk(false);
              const v = e.target.value;
              if (v !== String(deadline || '').slice(0, 10)) onDeadline(v);
            }}
            style={{ fontSize: 11.5, border: '1px solid #e2e8f0', borderRadius: 6, padding: '1px 4px' }}
          />
        ) : deadline ? (
          <Tooltip title={onDeadline ? 'Klik om de deadline aan te passen' : ''}>
            <Typography
              onClick={onDeadline ? (e) => { e.stopPropagation(); setDlBewerk(true); } : undefined}
              sx={{ fontSize: 11.5, color: '#b91c1c', fontWeight: 700,
                cursor: onDeadline ? 'pointer' : 'default',
                '&:hover': onDeadline ? { textDecoration: 'underline' } : {} }}>
              deadline {euDatum(deadline)}
            </Typography>
          </Tooltip>
        ) : onDeadline ? (
          <Typography onClick={(e) => { e.stopPropagation(); setDlBewerk(true); }}
            sx={{ fontSize: 11.5, color: '#94a3b8', cursor: 'pointer', '&:hover': { color: '#b91c1c' } }}>
            ＋ deadline
          </Typography>
        ) : null}
        <SignaalChips signalen={signalen} />
        {remarks ? (
          <Tooltip title={remarks}><Typography noWrap sx={{ fontSize: 11.5, color: '#94a3b8', maxWidth: 380 }}>{remarks}</Typography></Tooltip>
        ) : null}
        <Box sx={{ flex: 1 }} />
        {onNieuweTaak && (
          <Tooltip title="Taak toevoegen aan deze deliverable">
            <Typography component="span" onClick={(e) => { e.stopPropagation(); onNieuweTaak(); }}
              sx={{ fontSize: 13, cursor: 'pointer', opacity: 0.55, '&:hover': { opacity: 1 } }}>➕</Typography>
          </Tooltip>
        )}
        {onDupliceer && (
          <Tooltip title="Deliverable dupliceren, met al zijn open taken">
            <Box component="span" role="button" aria-label={`${titel} dupliceren`}
              onClick={(e) => { e.stopPropagation(); onDupliceer(); }}
              sx={{ display: 'inline-flex', cursor: 'pointer', color: '#64748b', opacity: 0.55, '&:hover': { opacity: 1 } }}>
              <ContentCopyIcon sx={{ fontSize: 14 }} />
            </Box>
          </Tooltip>
        )}
        {onPlanning && (
          <Tooltip title="Planning verschuiven / kopiëren / mailen">
            <Typography component="span" onClick={(e) => { e.stopPropagation(); onPlanning(); }}
              sx={{ fontSize: 13, cursor: 'pointer', opacity: 0.55, '&:hover': { opacity: 1 } }}>📅</Typography>
          </Tooltip>
        )}
        <Typography sx={{ fontSize: 11.5, color: '#64748b', fontWeight: 700 }}>
          {dicht ? `${taken.length} taken` : ''}{dicht && uren > 0 ? ' · ' : ''}{uren > 0 ? `${uren} u` : ''}
        </Typography>
      </Box>
      {dicht || !taken.length ? null : <TakenGrid taken={taken} onBewerk={onBewerk} onNieuw={onNieuw} onVerwijder={onVerwijder} metKop={metKop} />}
    </Box>
  );
}

function ProjectBlok({ project, open, onToggle, data, laden, filters, dichteGroepen, onToggleGroep, onBewerk, onNieuweRijen, onVerwijderTaak, onDupliceerDeliverable, onOpenProject, onPlanning, onNieuweTaak, onDeliverableDeadline, onOpenDeliverable, stoplicht, kopExtra = null, deliverableSignalen = null }) {
  const breedtes = useKolombreedtes();
  const zichtbaar = useMemo(() => {
    if (!data) return [];
    return data.taken.filter((t) => {
      if (filters.verbergAfgerond && (t.afgerond || t.prio === 'Afgerond' || t.prio === 'Opgestuurd')) return false;
      if (filters.assignee && !veldStr(t.assignee).toLowerCase().includes(filters.assignee.toLowerCase())) return false;
      if (filters.prio && t.prio !== filters.prio) return false;
      return true;
    });
  }, [data, filters]);

  const groepen = useMemo(() => {
    if (!data) return [];
    // Taken zonder deliverable groeperen we op hun TYPE (Script, Preproductie,
    // Montage…) — zoals de Airtable-interface deed; één bak '(Zonder
    // deliverable)' zei niets (feedback 31 aug). Vaste fase-volgorde bovenaan.
    const TYPE_VOLGORDE = ['Preproductie', 'Script', 'Regie', 'Illustratie', 'Draaidag',
      'Montage', 'Animatie', 'Grading', 'Audio', 'Projectafronding', 'Archief'];
    const perGroep = new Map();
    for (const t of zichtbaar) {
      const key = t.deliverableId || `type:${veldStr(t.type) || 'Overig'}`;
      if (!perGroep.has(key)) perGroep.set(key, []);
      perGroep.get(key).push(t);
    }
    const info = new Map(data.deliverables.map((d) => [d.id, d]));
    const uit = [...perGroep.entries()].map(([id, taken]) => {
      const isType = id.startsWith('type:');
      const typeNaam = isType ? id.slice(5) : '';
      const d = isType ? null : info.get(id);
      return {
        id: id || 'zonder',
        titel: isType ? (typeNaam === 'Overig' ? 'Overige taken' : typeNaam)
          : d ? `${d.nummer ? `${d.nummer}. ` : ''}${d.naam}` : '(Zonder deliverable)',
        sort: d?.nummer || '',
        typeRang: isType ? (TYPE_VOLGORDE.indexOf(typeNaam) === -1 ? 99 : TYPE_VOLGORDE.indexOf(typeNaam)) : null,
        deadline: d?.deadline || null,
        remarks: d?.deadlineRemarks || '',
        taken: taken.sort((a, b) => String(a.start || '9999').localeCompare(String(b.start || '9999'))),
      };
    });
    // Type-groepen eerst (fase-volgorde), daarna deliverables op nummer.
    return uit.sort((a, b) => {
      if (a.typeRang !== null && b.typeRang !== null) return a.typeRang - b.typeRang;
      if (a.typeRang !== null) return -1;
      if (b.typeRang !== null) return 1;
      return String(a.sort).localeCompare(String(b.sort), undefined, { numeric: true });
    });
  }, [data, zichtbaar]);

  const totaal = zichtbaar.reduce((s, t) => s + (typeof t.uren === 'number' ? t.uren : 0), 0);

  return (
    <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 2, mb: 1.25, bgcolor: '#fff' }}>
      {/* Sticky: projectkop + kolomkoppen blijven staan bij scrollen (Joris, 1 sep).
          Let op: geen overflow:hidden op de wrapper, dat breekt position:sticky. */}
      <Box onClick={onToggle} sx={{
        display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, cursor: 'pointer',
        bgcolor: open ? '#f1f5f9' : '#fff', '&:hover': { bgcolor: '#f1f5f9' },
        position: 'sticky', top: 0, zIndex: 3, borderRadius: '8px 8px 0 0',
        borderBottom: '1px solid #e2e8f0',
      }}>
        {open ? <ExpandMoreIcon sx={{ fontSize: 18, color: '#64748b' }} /> : <ChevronRightIcon sx={{ fontSize: 18, color: '#64748b' }} />}
        <Typography sx={{ fontSize: 13.5, fontWeight: 800, fontFamily: FONT, color: '#0f172a' }}>
          {project.nummer ? `${project.nummer}. ` : ''}{project.naam}
        </Typography>
        {project.klant ? (
          <Typography noWrap sx={{ fontSize: 12.5, color: '#94a3b8', fontFamily: FONT, maxWidth: 260 }}>
            {project.klant}
          </Typography>
        ) : null}
        {project.status && project.status !== 'In progress'
          ? <Chip label={project.status} size="small" sx={{ height: 20, fontSize: 11, bgcolor: '#fef3c7', color: '#92400e' }} />
          : null}
        {project.pl ? <Chip label={project.pl} size="small" sx={{ height: 20, fontSize: 11 }} /> : null}
        {kopExtra}
        <Tooltip title="Open projectpagina">
          <Box component="span" role="button" aria-label="Open projectpagina"
            onClick={(e) => { e.stopPropagation(); onOpenProject(project.id); }}
            sx={{ display: 'inline-flex', alignItems: 'center', color: '#94a3b8', cursor: 'pointer',
              p: 0.25, borderRadius: 1, '&:hover': { color: '#526075', bgcolor: '#e2e8f0' } }}>
            <OpenInNewIcon sx={{ fontSize: 15 }} />
          </Box>
        </Tooltip>
        <WijzigingslogKnop projectId={project.id} titel={`${project.nummer}. ${project.naam}`} sx={{ p: 0.25 }} />
        {(project.grippProjectId || project.grippId) && (
          <Tooltip title="Open in Gripp">
            <Box component="a" href="#gripp"
              onClick={(e) => {
                e.preventDefault(); e.stopPropagation();
                // Eerst checken of het record nog bestaat: Airtable-GrippID's
                // wijzen soms naar verwijderde offertes → witte pagina (9591).
                const type = project.grippProjectId ? 'project' : 'offer';
                const gid = project.grippProjectId || project.grippId;
                const url = project.grippProjectId
                  ? buildGrippProjectUrl(project.grippProjectId, project.grippCompanyId)
                  : buildGrippOfferUrl(project.grippId, project.grippCompanyId);
                openGrippGecontroleerd({
                  type, grippId: gid, url, authFetch, aiEngineUrl: AI_ENGINE_URL,
                  onWeg: () => toast.error(`Dit ${type === 'offer' ? 'offerte' : 'project'}-record bestaat niet (meer) in Gripp`),
                });
              }}
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, textDecoration: 'none',
                px: 0.6, py: 0.1, borderRadius: 1, fontSize: 10.5, fontWeight: 800,
                color: '#7c3aed', bgcolor: '#f5f3ff', '&:hover': { bgcolor: '#ede9fe' } }}>
              Gripp<OpenInNewIcon sx={{ fontSize: 11 }} />
            </Box>
          </Tooltip>
        )}
        <Box sx={{ flex: 1 }} />
        {onNieuweTaak && (
          <Tooltip title="Taak toevoegen op projectniveau (zonder deliverable)">
            <Typography component="span"
              onClick={(e) => { e.stopPropagation(); onNieuweTaak(project, null); }}
              sx={{ fontSize: 14, cursor: 'pointer', opacity: 0.5, mr: 0.5, '&:hover': { opacity: 1 } }}>➕</Typography>
          </Tooltip>
        )}
        {stoplicht ? <StoplichtRij status={stoplicht} /> : null}
        {open && data ? (
          <Typography sx={{ fontSize: 12, color: '#64748b', fontWeight: 700 }}>
            {zichtbaar.length} taken · {totaal} u
          </Typography>
        ) : null}
      </Box>
      {open ? (
        laden ? (
          <Box sx={{ py: 3, textAlign: 'center', borderTop: '1px solid #e2e8f0' }}><CircularProgress size={20} /></Box>
        ) : data ? (
          groepen.length ? (
            // Eigen horizontale scroll met vaste kolombreedtes: de layout van
            // de rest van de pagina beweegt hierdoor nooit mee.
            <Box sx={{ overflowX: 'auto', borderTop: '1px solid #e2e8f0', borderRadius: '0 0 8px 8px' }}>
              <Box sx={{ minWidth: tabelMinBreedte(breedtes), width: '100%' }}>
                {groepen.map((g, gi) => (
                  <DeliverableGroep key={g.id} signalen={deliverableSignalen?.[g.id]} titel={g.titel} deadline={g.deadline} remarks={g.remarks} metKop={gi === 0}
                    onDeadline={onDeliverableDeadline && String(g.id || '').startsWith('rec')
                      ? (iso) => onDeliverableDeadline(project.id, g.id, iso) : undefined}
                    taken={g.taken}
                    dicht={dichteGroepen.has(`${project.id}:${g.id}`)}
                    onToggle={() => onToggleGroep(`${project.id}:${g.id}`)}
                    onBewerk={onBewerk}
                    onNieuw={onNieuweRijen ? (rijen) => onNieuweRijen(project, g, rijen) : undefined}
                    onVerwijder={onVerwijderTaak ? (taak) => onVerwijderTaak(project, taak) : undefined}
                    onDupliceer={onDupliceerDeliverable && String(g.id || '').startsWith('rec') ? () => onDupliceerDeliverable(project, g) : undefined}
                    onPlanning={onPlanning ? () => onPlanning(project, g, groepen) : undefined}
                    onNieuweTaak={onNieuweTaak ? () => onNieuweTaak(project, g) : undefined}
                    onOpen={onOpenDeliverable && String(g.id || '').startsWith('rec') ? () => onOpenDeliverable(project.id, g.id) : undefined} />
                ))}
              </Box>
            </Box>
          ) : (
            <Typography sx={{ p: 2, fontSize: 13, color: '#94a3b8', borderTop: '1px solid #e2e8f0' }}>
              Geen taken (binnen de huidige filters).
            </Typography>
          )
        ) : (
          <Typography sx={{ p: 2, fontSize: 13, color: '#b91c1c', borderTop: '1px solid #e2e8f0' }}>
            Taken laden mislukt — klap dicht en opnieuw open.
          </Typography>
        )
      ) : null}
    </Box>
  );
}

/**
 * Ook ingebed te gebruiken (Planning → Nog in te plannen, deadline-check):
 * `projectIds` beperkt en ordent de lijst, `kopExtra(project)` zet iets in de
 * projectkop (signalen), `deliverableSignalen` {deliverableId: [codes]} komt
 * naast de deadline, `onGewijzigd` meldt een geslaagde wijziging aan de host.
 */
// Ingebed (Nog in te plannen → Deadline-check) gaat het om deliverables: open het project
// met het deliverables-paneel (wiki 27 sep). Losstaand: gewoon het project.
export const projectLinkOverzicht = (recId, ingebed) => `/projects/${recId}${ingebed ? '?paneel=deliverables' : ''}`;

export default function OverzichtPerProject({ ingebed = false, projectIds = null, kopExtra = null, deliverableSignalen = null, onGewijzigd = null } = {}) {
  const { permissions } = useUserRole();
  const [projecten, setProjecten] = useState([]);
  const [laadFout, setLaadFout] = useState(null);
  const [bezig, setBezig] = useState(true);
  const [zoek, setZoek] = useState('');
  const [open, setOpen] = useState(() => new Set());
  const [perProject, setPerProject] = useState({});   // recId -> { taken, deliverables }
  const [ladend, setLadend] = useState(() => new Set());
  const [dichteGroepen, setDichteGroepen] = useState(() => new Set());
  const [filters, setFilters] = useState({ assignee: '', prio: '', verbergAfgerond: true, verbergOffertes: false, pl: '' });

  useEffect(() => {
    let actueel = true;
    (async () => {
      try {
        // Met 'Verberg afgerond' uitgevinkt horen ook Afgerond/Gearchiveerd erbij —
        // de server laat die standaard weg, dus dan expliciet meevragen (9508, 1 sep).
        const metArchief = !filters.verbergAfgerond;
        const r = await authFetch(`${BACKEND_URL}/Api/projects${metArchief ? '?includeArchived=true' : ''}`);
        const d = await leesJson(r, 'Projecten laden');
        if (!actueel) return;
        const statussen = ['In progress', 'Nog niet gestart', 'Offerte', 'Lead',
          ...(metArchief ? ['Afgerond', 'On Hold', 'Gearchiveerd', 'Geannuleerd'] : [])];
        const lijst = (d.projects || [])
          // Ook Offerte/Lead: planning begint vaak al in de offertefase
          // (9753/9754 misten hierdoor — feedback 26 aug).
          .filter((p) => statussen.includes(p.fields?.Status))
          .map((p) => ({
            id: p.id,
            nummer: veldStr(p.fields?.Projectnummer),
            naam: p.fields?.Projectnaam || veldStr(p.fields?.Projectnr_en_naam) || 'Zonder naam',
            klant: veldStr(p.fields?.KlantnaamString || p.fields?.Klantnaam),
            status: p.fields?.Status || '',
            pl: veldStr(p.fields?.['Projectleider (Voornaam)'] || p.fields?.['Projectleider (Volledige naam)']),
            grippId: p.fields?.GrippID || null,
            grippProjectId: p.fields?.GrippProjectID || null,
            grippCompanyId: p.fields?.GrippCompanyID || null,
            origFields: p.fields || {},
          }))
          .sort((a, b) => String(b.nummer).localeCompare(String(a.nummer), undefined, { numeric: true }));
        setProjecten(lijst);
      } catch (e) {
        if (actueel) setLaadFout(e.message);
      } finally {
        if (actueel) setBezig(false);
      }
    })();
    return () => { actueel = false; };
  }, [filters.verbergAfgerond]);

  // 📅 Deliverable-planning verschuiven/kopiëren + mail-dialoog (Joris, 1 sep).
  const { instance: msalInstance, accounts: msalAccounts } = useMsal();
  const [planningDlg, setPlanningDlg] = useState(null);
  const [taakDlg, setTaakDlg] = useState(null); // { projectId, deliverableId, titel }
  const [mailDlg, setMailDlg] = useState(null); // { project, taken, deadline }
  const pasPlanningToe = async (wijzigingen) => {
    const dlg = planningDlg;
    if (!dlg || !wijzigingen.length) return;
    const uitkomsten = [];
    const patchEen = async ({ taak, velden }) => {
      try {
        const r = await authFetch(`${BACKEND_URL}/Api/tasks/${encodeURIComponent(taak.id)}`, {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(velden),
        });
        return r.ok;
      } catch { return false; }
    };
    // In groepjes van 4: snel genoeg, zonder Airtable-limieten te raken.
    for (let i = 0; i < wijzigingen.length; i += 4) {
      // eslint-disable-next-line no-await-in-loop
      uitkomsten.push(...await Promise.all(wijzigingen.slice(i, i + 4).map(patchEen)));
    }
    const fouten = uitkomsten.filter((ok) => !ok).length;
    if (fouten) toast.error(`${fouten} taken konden niet worden bijgewerkt`);
    else toast.success(`Planning bijgewerkt (${wijzigingen.length} taken)`);
    laadTaken(dlg.projectId);
  };

  // Deliverable-popup vanuit de groepskop: het record komt uit het projectoverzicht
  // (zelfde vorm als op de projectpagina), dus de bestaande dialoog past 1-op-1.
  const [deliverableDlg, setDeliverableDlg] = useState(null); // { projectId, deliverable } | { projectId, laden: true }
  const openDeliverable = async (projectId, deliverableId) => {
    setDeliverableDlg({ projectId, laden: true });
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/project-overview/${encodeURIComponent(projectId)}`);
      const d = await leesJson(r, 'Projectoverzicht laden');
      const rec = (d.deliverables || []).find((x) => x.id === deliverableId);
      if (!rec) throw new Error('Deliverable niet gevonden');
      setDeliverableDlg({ projectId, deliverable: rec });
    } catch (e) { toast.error(`Deliverable openen mislukt: ${e.message}`); setDeliverableDlg(null); }
  };

  // Volgnummer per project: een ouder antwoord (gestart vóór een latere wijziging) mag een
  // nieuwer antwoord nooit overschrijven.
  const laadVolgnr = useRef({});
  const laadTaken = async (projectId) => {
    const nr = (laadVolgnr.current[projectId] || 0) + 1;
    laadVolgnr.current[projectId] = nr;
    const actueel = () => laadVolgnr.current[projectId] === nr;
    setLadend((prev) => new Set(prev).add(projectId));
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/planning/project-taken?projectId=${encodeURIComponent(projectId)}`);
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      if (actueel()) setPerProject((prev) => ({ ...prev, [projectId]: d }));
    } catch {
      if (actueel()) setPerProject((prev) => ({ ...prev, [projectId]: null }));
    } finally {
      if (actueel()) setLadend((prev) => { const n = new Set(prev); n.delete(projectId); return n; });
    }
  };

  const wijzigDeliverableDeadline = async (projectId, deliverableId, iso) => {
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/deliverables/${encodeURIComponent(deliverableId)}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ Deadline: iso || null }),
      });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.error || `HTTP ${r.status}`); }
      toast.success(iso ? 'Deadline bijgewerkt' : 'Deadline verwijderd');
      laadTaken(projectId);
      onGewijzigd?.();
    } catch (e) {
      toast.error(`Deadline aanpassen mislukt: ${e.message}`);
    }
  };

  const toggle = (projectId) => {
    setOpen((prev) => {
      const n = new Set(prev);
      if (n.has(projectId)) { n.delete(projectId); return n; }
      n.add(projectId);
      return n;
    });
    if (perProject[projectId] === undefined && !ladend.has(projectId)) laadTaken(projectId);
  };

  // Alles uit-/inklappen: alle zichtbare (gefilterde) projecten openen en hun
  // taken laden — met 4 tegelijk om Airtable niet plat te slaan.
  const [bulkLaden, setBulkLaden] = useState(false);
  const klapAllesUit = async (doel) => {
    setOpen(new Set(doel.map((p) => p.id)));
    const teLaden = doel.filter((p) => perProject[p.id] === undefined && !ladend.has(p.id));
    if (!teLaden.length) return;
    setBulkLaden(true);
    try {
      const rij = [...teLaden];
      await Promise.all(Array.from({ length: 4 }, async () => {
        while (rij.length) { const p = rij.shift(); await laadTaken(p.id); }
      }));
    } finally { setBulkLaden(false); }
  };

  // Stoplicht-statussen: per zichtbaar project in batches van 60 uit de engine.
  const [stoplichten, setStoplichten] = useState({});
  const stoplichtBezig = useRef(new Set());
  const laadStoplichten = async (ids) => {
    const nieuw = ids.filter((id) => stoplichten[id] === undefined && !stoplichtBezig.current.has(id));
    if (!nieuw.length) return;
    nieuw.forEach((id) => stoplichtBezig.current.add(id));
    const uit = await fetchProjectStatussen(nieuw);
    setStoplichten((prev) => ({ ...prev, ...uit }));
  };

  const toggleGroep = (key) => {
    setDichteGroepen((prev) => {
      const n = new Set(prev);
      if (n.has(key)) n.delete(key); else n.add(key);
      return n;
    });
  };

  // Alles in/uitklappen: verzamel alle groep-keys van de geladen projecten.
  const alleGroepKeys = useMemo(() => {
    const keys = [];
    for (const [pid, d] of Object.entries(perProject)) {
      if (!d) continue;
      const ids = new Set((d.taken || []).map((t) => t.deliverableId || 'zonder'));
      ids.forEach((id) => keys.push(`${pid}:${id === 'zonder' ? 'zonder' : id}`));
    }
    return keys;
  }, [perProject]);
  const allesDicht = alleGroepKeys.length > 0 && alleGroepKeys.every((k) => dichteGroepen.has(k));

  // Stijlcorrecties op react-datasheet-grid, één keer in de pagina.
  useEffect(() => {
    if (document.getElementById('grid-stijl')) return;
    const stijl = document.createElement('style'); stijl.id = 'grid-stijl'; stijl.textContent = GRID_STIJL; document.head.appendChild(stijl);
  }, []);

  /**
   * Inline bewerken: optimistic in de lokale state, dan PATCH via de bestaande
   * taken-route (vlag-flip PG/Airtable zit dáár). Mislukt → terugdraaien.
   */
  // Ongedaan maken (⌘Z) en opnieuw (⌘⇧Z / Ctrl+Y), meerdere stappen diep. Elke
  // bewerking legt zijn omkering vast; een plak- of doortrekactie deelt één batch
  // en gaat dus in één keer terug.
  const undoRef = useRef([]);
  const redoRef = useRef([]);
  // Geschiedenis: één ⌘Z/⌘⇧Z tegelijk, en een generatieteller. Een nieuwe wijziging wist
  // 'opnieuw'; een ongedaan-stap die nog liep mag daarna niets terugzetten op die stapel.
  const geschiedenis = useRef({ bezig: false, gen: 0 });
  const nieuweWijziging = () => { redoRef.current = []; geschiedenis.current.gen += 1; };
  // Een verwijderde taak komt terug met een nieuw id: oudere stappen wijzen daarna naar dat id.
  const hernoemInGeschiedenis = (oud, nieuw) => {
    [undoRef, redoRef].forEach((st) => st.current.forEach((it) => {
      if (it.taakId === oud) it.taakId = nieuw;
      if (it.aanmaak) {
        it.aanmaak.taken = it.aanmaak.taken.map((t) => ({
          ...t, ...(t.id === oud ? { id: nieuw } : {}), ...(t.wasId === oud ? { wasId: nieuw } : {}),
        }));
      }
      if (it.verwijdering) {
        if (it.verwijdering.oudId === oud) it.verwijdering.oudId = nieuw;
        if (it.verwijdering.nieuwId === oud) it.verwijdering.nieuwId = nieuw;
      }
    }));
  };
  const huidigeRij = (taakId) => {
    for (const pid of Object.keys(perProject)) {
      const t = (perProject[pid]?.taken || []).find((x) => x.id === taakId);
      if (t) return t;
    }
    return null;
  };
  // De omkering van een wijziging: welke velden moeten terug naar welke waarde.
  const omkering = (taak, lokaal) => {
    const velden = {}; const terug = {}; let nietTerug = null;
    Object.keys(lokaal).forEach((k) => {
      if (k === 'assignee' || k === 'submitter') return; // gaat via de id-velden
      if (k === 'assigneeId' || k === 'submitterId') {
        const naamVeld = k === 'assigneeId' ? 'assignee' : 'submitter';
        const oudId = taak[k] || zoekPersoon(taak[naamVeld])?.id;
        if (!oudId) { nietTerug = naamVeld; return; }
        Object.assign(velden, VELD_NAAR_AIRTABLE[k](oudId)); terug[k] = oudId; terug[naamVeld] = taak[naamVeld];
        if (k === 'assigneeId') terug.assigneeIds = [oudId]; // ongedaan zet één uitvoerder terug
        return;
      }
      if (!VELD_NAAR_AIRTABLE[k]) return;
      Object.assign(velden, VELD_NAAR_AIRTABLE[k](taak[k] ?? null)); terug[k] = taak[k] ?? null;
    });
    return { velden, terug, nietTerug };
  };
  const bewerkTaak = async (taak, airtableVelden, lokaal, opties = {}) => {
    const projectId = Object.keys(perProject).find((pid) =>
      (perProject[pid]?.taken || []).some((t) => t.id === taak.id));
    if (!projectId) {
      // Een ⌘Z/⌘⇧Z-stap op een taak die net (opnieuw) is aangemaakt en lokaal nog niet is
      // geladen: wel opslaan, en daarna de geopende projecten opnieuw laden (het volgnummer in
      // laadTaken zorgt dat een ouder antwoord deze stand niet meer overschrijft).
      if (!opties.stil) return;
      const r = await authFetch(`${BACKEND_URL}/Api/tasks/${encodeURIComponent(taak.id)}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(airtableVelden),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
      taakGewijzigd();
      await Promise.all(Object.keys(perProject).filter((pid) => perProject[pid]).map((pid) => laadTaken(pid)));
      return;
    }
    const vorigeTaak = (perProject[projectId]?.taken || []).find((t) => t.id === taak.id);
    if (!opties.stil) {
      const rij = huidigeRij(taak.id) || taak;
      const { velden, terug, nietTerug } = omkering(rij, lokaal);
      undoRef.current.push({ batch: opties.batch || `${Date.now()}-${Math.random()}`, taakId: taak.id, naam: rij.naam,
        vooruit: { velden: airtableVelden, lokaal }, terug: { velden, lokaal: terug }, nietTerug });
      if (undoRef.current.length > 100) undoRef.current.shift();
      nieuweWijziging(); // een nieuwe bewerking maakt 'opnieuw' ongeldig
    }
    setPerProject((prev) => ({
      ...prev,
      [projectId]: {
        ...prev[projectId],
        taken: prev[projectId].taken.map((t) => (t.id === taak.id ? { ...t, ...lokaal } : t)),
      },
    }));
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/tasks/${encodeURIComponent(taak.id)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(airtableVelden),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
      // Ook voor ⌘Z/⌘⇧Z (die gaan hierlangs): Rooster, Capaciteit, … laten hun stand vallen.
      taakGewijzigd();
    } catch (e) {
      // Alleen deze taak terug (Codex 2 sep): een volledige projectsnapshot draaide
      // ook geslaagde parallelle wijzigingen van andere rijen lokaal terug.
      if (vorigeTaak) {
        setPerProject((prev) => ({
          ...prev,
          [projectId]: { ...prev[projectId], taken: prev[projectId].taken.map((t) => (t.id === taak.id ? vorigeTaak : t)) },
        }));
      }
      toast.error(`Opslaan mislukt: ${e.message}`);
    }
  };

  // ── Nieuwe taken (dupliceren/plakken) en deliverable dupliceren ──
  const postTaak = async (payload) => {
    const r = await authFetch(`${BACKEND_URL}/Api/tasks`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(d.error || d.detail || `HTTP ${r.status}`);
    return d.task?.id || d.id || null;
  };
  // In groepjes van 4 (zoals het verschuiven): snel, zonder de Airtable-limiet te raken.
  // `gemaakt` = [{ payload, id }] van wat echt is aangemaakt (alleen dát kan terug/opnieuw).
  const maakTaken = async (payloads) => {
    const gemaakt = []; let fouten = 0;
    for (let i = 0; i < payloads.length; i += 4) {
      const groep = payloads.slice(i, i + 4);
      // eslint-disable-next-line no-await-in-loop
      const uit = await Promise.allSettled(groep.map(postTaak));
      const gelukt = uit.map((u, k) => (u.status === 'fulfilled' && u.value ? { payload: groep[k], id: u.value } : null)).filter(Boolean);
      gemaakt.push(...gelukt);
      fouten += uit.length - gelukt.length;
    }
    return { gemaakt, ids: gemaakt.map((g) => g.id), fouten };
  };
  /** Verwijdert de taken; geeft de id's terug die NIET weg konden (404 = al weg = gelukt). */
  const verwijderTaken = async (ids) => {
    const mislukt = [];
    for (let i = 0; i < ids.length; i += 4) {
      const groep = ids.slice(i, i + 4);
      // eslint-disable-next-line no-await-in-loop
      const uit = await Promise.allSettled(groep.map(async (id) => {
        const r = await authFetch(`${BACKEND_URL}/Api/tasks/${encodeURIComponent(id)}`, { method: 'DELETE' });
        if (!r.ok && r.status !== 404) throw new Error(`HTTP ${r.status}`);
      }));
      uit.forEach((u, k) => { if (u.status === 'rejected') mislukt.push(groep[k]); });
    }
    return mislukt;
  };
  // Verwijderen vraagt planner_todo:edit (aanmaken niet): alleen dan bieden we ongedaan aan.
  const magVerwijderen = Boolean(permissions?.planner_todo?.edit);
  const naWijziging = (projectId) => {
    const herladen = laadTaken(projectId);
    onGewijzigd?.();
    taakGewijzigd(); // Rooster, Capaciteit, Nog in te plannen, Dagstart: hun stand vervalt
    return herladen; // wie op de verse stand moet wachten (herstel), kan dat
  };
  /**
   * Aangemaakte taken weer weg (⌘Z of de knop in de melding). Wat niet weg kon blijft in
   * het item staan, zodat nog eens proberen alleen die resterende taken opnieuw probeert.
   * @returns {boolean} true als alles weg is
   */
  const draaiAanmaakTerug = async (item) => {
    const a = item.aanmaak;
    if (a.bezig) return false;
    a.bezig = true;
    try {
      const nogDaar = a.taken.filter((t) => t.id);
      const mislukt = new Set(await verwijderTaken(nogDaar.map((t) => t.id)));
      a.taken = a.taken.map((t) => (t.id && !mislukt.has(t.id) ? { ...t, id: null, wasId: t.id } : t));
      naWijziging(a.projectId);
      if (mislukt.size) throw new Error(`${mislukt.size} van de ${nogDaar.length} taken konden niet weg — probeer ⌘Z nog eens`);
      return true;
    } finally { a.bezig = false; }
  };
  /** Opnieuw (⌘⇧Z): precies de taken die eerder gelukt en daarna teruggedraaid zijn. */
  const maakAanmaakOpnieuw = async (item) => {
    const a = item.aanmaak;
    const { gemaakt, fouten } = await maakTaken(a.taken.map((t) => t.payload));
    naWijziging(a.projectId);
    if (!gemaakt.length) throw new Error('geen enkele taak kon opnieuw worden aangemaakt');
    // Nieuwe id's: latere stappen (bv. het verwijderen van zo'n taak) wijzen voortaan daarnaar.
    // Wat niet lukte blijft in de lijst staan (zonder id), zodat er niets uit de geschiedenis valt.
    const vorige = a.taken;
    a.taken = vorige.map((t) => {
      const g = gemaakt.find((x) => x.payload === t.payload);
      return g ? { payload: t.payload, id: g.id } : t;
    });
    gemaakt.forEach((g) => {
      const oud = vorige.find((t) => t.payload === g.payload);
      if (oud?.wasId) hernoemInGeschiedenis(oud.wasId, g.id);
    });
    if (fouten) {
      // Latere 'opnieuw'-stappen gaan uit van taken die nu niet bestaan: die vervallen.
      redoRef.current = [];
      toast.error(`${fouten} ${fouten === 1 ? 'taak kon' : 'taken konden'} niet opnieuw worden aangemaakt — verdere stappen opnieuw vervallen`);
    }
    return true;
  };
  const meldAanmaak = (projectId, gemaakt, tekst) => {
    if (!magVerwijderen) {
      toast.success(`${tekst} — ongedaan maken kan hier niet: taken verwijderen vraagt het recht om te plannen`, { duration: 6000 });
      return;
    }
    const item = { batch: `${Date.now()}-${Math.random()}`, naam: tekst, aanmaak: { projectId, taken: gemaakt, bezig: false } };
    undoRef.current.push(item);
    if (undoRef.current.length > 100) undoRef.current.shift();
    nieuweWijziging();
    biedOngedaanAan(tekst, () => viaMelding(item, () => draaiAanmaakTerug(item)), { eigenKnop: true });
  };
  const nieuweRijen = async (project, groep, rijen) => {
    await laadPickerPersonen().catch(() => {}); // voor de naam → persoon-koppeling
    const deliverableId = groep && String(groep.id || '').startsWith('rec') ? groep.id : null;
    const payloads = rijen.map((r) => naarNieuweTaak(r, { projectId: project.id, deliverableId })).filter(Boolean);
    if (!payloads.length) return;
    const { gemaakt, fouten } = await maakTaken(payloads);
    naWijziging(project.id);
    if (fouten) toast.error(`${fouten} ${fouten === 1 ? 'taak kon' : 'taken konden'} niet worden aangemaakt`);
    if (gemaakt.length) meldAanmaak(project.id, gemaakt, gemaakt.length === 1 ? `Taak toegevoegd: ${gemaakt[0].payload.NaamTaak}` : `${gemaakt.length} taken toegevoegd`);
  };
  const dupliceerDeliverable = async (project, groep) => {
    const data = perProject[project.id];
    const d = (data?.deliverables || []).find((x) => x.id === groep.id);
    // Alle open taken van de deliverable, ook die de filters nu verbergen.
    const bron = (data?.taken || []).filter((t) => t.deliverableId === groep.id && !isAfgerond(t));
    const naam = `${d?.naam || groep.titel} (kopie)`;
    const ok = await bevestig({
      titel: 'Deliverable dupliceren?',
      tekst: `Maakt een nieuwe deliverable "${naam}" met ${bron.length} ${bron.length === 1 ? 'taak' : 'taken'} (zelfde namen, uitvoerders, uren en datums; afgeronde taken niet). De taken zijn daarna ongedaan te maken; de deliverable zelf verwijder je in de deliverable-popup of Airtable.`,
      knop: 'Dupliceren',
    });
    if (!ok) return;
    try {
      await laadPickerPersonen().catch(() => {});
      const r = await authFetch(`${BACKEND_URL}/Api/deliverables`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 'Naam van Deliverable': naam, Project: [project.id], ...(d?.typeDeadline ? { TypeDeadline: d.typeDeadline } : {}) }),
      });
      const res = await leesJson(r, 'Deliverable dupliceren');
      const nieuwId = res?.deliverable?.id;
      if (!nieuwId) throw new Error('geen id terug van de engine');
      const payloads = bron.map((t) => naarNieuweTaak(t, { projectId: project.id, deliverableId: nieuwId })).filter(Boolean);
      const { gemaakt, fouten } = await maakTaken(payloads);
      naWijziging(project.id);
      if (fouten) toast.error(`${fouten} ${fouten === 1 ? 'taak kon' : 'taken konden'} niet worden gekopieerd`);
      if (gemaakt.length) meldAanmaak(project.id, gemaakt, `"${naam}" gemaakt met ${gemaakt.length} ${gemaakt.length === 1 ? 'taak' : 'taken'} — verschuif de planning met 📅`);
      else toast.success(`"${naam}" gemaakt`);
    } catch (e) {
      meldFout(e, 'Deliverable dupliceren');
    }
  };

  /** Ongedaan maken via de knop in de melding: zelfde regels als ⌘Z (één tegelijk, generatie). */
  const viaMelding = async (item, terugdraaien) => {
    if (!undoRef.current.includes(item)) return; // al via ⌘Z teruggedraaid
    // Alleen de laatste stap: een oudere melding overslaan liet latere stappen (bv. het
    // verwijderen van een van deze taken) zonder grond achter.
    if (undoRef.current[undoRef.current.length - 1] !== item) {
      toast('Er is intussen iets anders gewijzigd — maak eerst dat ongedaan met ⌘Z', { duration: 3000 });
      return;
    }
    if (geschiedenis.current.bezig) { toast('Even wachten, er wordt nog iets teruggedraaid', { duration: 1500 }); return; }
    geschiedenis.current.bezig = true;
    const gen = geschiedenis.current.gen;
    try {
      if (!(await terugdraaien())) return; // niet gelukt: item blijft op de stapel
      undoRef.current = undoRef.current.filter((x) => x !== item);
      if (geschiedenis.current.gen === gen) redoRef.current.push(item);
    } finally { geschiedenis.current.bezig = false; }
  };

  // ── Een bestaande taak verwijderen (knop per regel) ──
  // Ongedaan maken kan alleen door hem opnieuw aan te maken: een nieuwe taak met een nieuw
  // id en dezelfde planvelden. Dat zeggen we eerlijk in de bevestiging en de melding.
  const draaiVerwijderingTerug = async (item) => {
    const v = item.verwijdering;
    if (v.bezig) return false;
    v.bezig = true;
    try {
      if (!v.payload) throw new Error('deze taak had geen naam en kan niet terug');
      const { gemaakt } = await maakTaken([v.payload]);
      if (!gemaakt.length) { naWijziging(v.projectId); throw new Error('de taak kon niet opnieuw worden aangemaakt'); }
      const nieuwId = gemaakt[0].id;
      // Eerst vastleggen: wat hierna misgaat mag nooit tot een tweede aanmaak leiden.
      hernoemInGeschiedenis(v.oudId, nieuwId);
      v.oudId = nieuwId;
      v.nieuwId = nieuwId;
      // Het afgerond-vinkje kan niet mee bij aanmaken; apart terugzetten.
      if (v.afgerond) {
        let ok = false;
        try {
          const r = await authFetch(`${BACKEND_URL}/Api/tasks/${encodeURIComponent(nieuwId)}`, {
            method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ Afgerond: true }),
          });
          ok = r.ok;
        } catch { ok = false; }
        if (!ok) toast.error('Taak teruggezet, maar het afgerond-vinkje kon niet terug — vink hem opnieuw af');
      }
      // Wachten op de verse stand: de volgende ⌘Z (pas ná deze stap) vindt de taak dan lokaal.
      await naWijziging(v.projectId);
      return true;
    } finally { v.bezig = false; }
  };
  const verwijderOpnieuw = async (item) => {
    const v = item.verwijdering;
    if (!v.nieuwId) return true;
    if (v.bezig) return false;
    v.bezig = true;
    try {
      const mislukt = await verwijderTaken([v.nieuwId]);
      if (!mislukt.length) inVerwijdering.current.add(v.nieuwId); // weg: de knop op de oude regel doet niets meer
      await naWijziging(v.projectId);
      if (mislukt.length) throw new Error('de taak kon niet opnieuw worden verwijderd');
      v.nieuwId = null;
      return true;
    } finally { v.bezig = false; }
  };
  // Per taak één verwijderactie: vanaf de eerste klik tot hij echt weg is. Een tweede klik of
  // ⌘⌫ op dezelfde (nog zichtbare) regel maakte anders een tweede herstelstap.
  const inVerwijdering = useRef(new Set());
  const verwijderTaak = async (project, taak) => {
    if (inVerwijdering.current.has(taak.id)) return;
    // Niet door een lopende ⌘Z/⌘⇧Z heen (die kan dezelfde taak net aanmaken of verwijderen).
    if (geschiedenis.current.bezig) { toast('Even wachten, er wordt nog iets teruggedraaid', { duration: 1500 }); return; }
    inVerwijdering.current.add(taak.id);
    let weg = false;
    try { weg = await verwijderTaakEenmaal(project, taak); } finally { if (!weg) inVerwijdering.current.delete(taak.id); }
  };
  /** @returns {boolean} true als de taak echt verwijderd is (dan blijft zijn id geblokkeerd). */
  const verwijderTaakEenmaal = async (project, taak) => {
    const naam = String(taak.naam || '').trim() || 'deze taak';
    const ok = await bevestig({
      titel: 'Taak verwijderen?',
      tekst: `"${naam}" wordt verwijderd. Ongedaan maken (⌘Z of de knop in de melding) maakt hem opnieuw aan als nieuwe taak met dezelfde naam, uitvoerder, prio, datums, uren, deadline en type. Wat er verder aan hing (opmerkingen, subtaken, feedback, uren) komt niet terug.`,
      knop: 'Verwijderen', gevaarlijk: true,
    });
    if (!ok) return false;
    await laadPickerPersonen().catch(() => {}); // voor de uitvoerder in de herstel-payload
    // Ook een lokale (Postgres-)deliverable gaat mee, anders komt de taak in een andere groep terug.
    const deliverableId = /^(rec|loc_deliv_)/.test(String(taak.deliverableId || '')) ? taak.deliverableId : null;
    const basis = naarNieuweTaak(taak, { projectId: project.id, deliverableId });
    // Herstel = de taak zoals hij was: eigen prio (ook Afgerond/Opgestuurd) en álle uitvoerders.
    const ids = (taak.assigneeIds || []).filter(Boolean);
    let payload = null;
    if (basis) {
      payload = { ...basis, PrioNr: taak.prio || 'Geen prio' };
      if (ids.length) { payload.Assignee = ids; delete payload.zonderAssignee; }
    }
    // Hier telt 404 níét als gelukt (anders dan bij ongedaan maken): was de taak al weg (bv. via
    // ⌘⇧Z terwijl de regel nog in beeld stond), dan is er niets om later terug te zetten.
    let status = 0;
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/tasks/${encodeURIComponent(taak.id)}`, { method: 'DELETE' });
      status = r.status;
    } catch { status = 0; }
    if (status === 404) { naWijziging(project.id); toast(`"${naam}" was al verwijderd`, { duration: 2500 }); return true; }
    if (status < 200 || status >= 300) { toast.error(`Verwijderen mislukt: ${naam}`); return false; }
    naWijziging(project.id);
    if (!payload) { nieuweWijziging(); toast.success(`Verwijderd: ${naam}`); return true; }
    const item = { batch: `${Date.now()}-${Math.random()}`, naam: `"${naam}" verwijderd`,
      verwijdering: { projectId: project.id, payload, afgerond: Boolean(taak.afgerond), oudId: taak.id, nieuwId: null, bezig: false } };
    undoRef.current.push(item);
    if (undoRef.current.length > 100) undoRef.current.shift();
    nieuweWijziging();
    biedOngedaanAan(`Verwijderd: ${naam} — ongedaan maken zet hem terug als nieuwe taak`,
      () => viaMelding(item, () => draaiVerwijderingTerug(item)), { eigenKnop: true });
    return true;
  };

  const speelTerug = async (stapel, richting, gen = geschiedenis.current.gen) => {
    const laatste = stapel.current[stapel.current.length - 1];
    if (!laatste) { toast(richting === 'undo' ? 'Niets om ongedaan te maken' : 'Niets om opnieuw te doen', { duration: 900 }); return []; }
    const stap = [];
    while (stapel.current.length && stapel.current[stapel.current.length - 1].batch === laatste.batch) stap.push(stapel.current.pop());
    let overgeslagen = 0;
    const gelukt = [];
    const terugOpStapel = [];
    for (const item of stap) {
      if (item.verwijdering) {
        try {
          // eslint-disable-next-line no-await-in-loop
          if (await (richting === 'undo' ? draaiVerwijderingTerug(item) : verwijderOpnieuw(item))) gelukt.push(item);
          else terugOpStapel.push(item);
        } catch (e) {
          terugOpStapel.push(item);
          toast.error(`${richting === 'undo' ? 'Ongedaan maken' : 'Opnieuw'} mislukt: ${e.message}`);
        }
        continue;
      }
      if (item.aanmaak) {
        // Alleen een geslaagde aanmaak-stap verhuist naar de andere stapel; een (deels)
        // mislukte blijft staan, zodat nog eens ⌘Z alleen de rest probeert en ⌘⇧Z niets verdubbelt.
        try {
          // eslint-disable-next-line no-await-in-loop
          if (await (richting === 'undo' ? draaiAanmaakTerug(item) : maakAanmaakOpnieuw(item))) gelukt.push(item);
          else terugOpStapel.push(item);
        } catch (e) {
          terugOpStapel.push(item);
          toast.error(`${richting === 'undo' ? 'Ongedaan maken' : 'Opnieuw'} mislukt: ${e.message}`);
        }
        continue;
      }
      const doel = richting === 'undo' ? item.terug : item.vooruit;
      if (!Object.keys(doel.velden).length) { gelukt.push(item); overgeslagen += 1; continue; }
      try {
        // eslint-disable-next-line no-await-in-loop
        await bewerkTaak({ id: item.taakId }, doel.velden, doel.lokaal, { stil: true });
        gelukt.push(item);
      } catch (e) {
        terugOpStapel.push(item);
        toast.error(`${richting === 'undo' ? 'Ongedaan maken' : 'Opnieuw'} mislukt: ${e.message}`);
      }
    }
    if (overgeslagen) toast.error(`${overgeslagen} cel${overgeslagen === 1 ? '' : 'len'} kon niet terug (persoon niet meer gevonden)`);
    // Een nieuwe wijziging wist 'opnieuw': een mislukte redo-stap hoort daar dan niet meer.
    if (richting === 'undo' || geschiedenis.current.gen === gen) stapel.current.push(...terugOpStapel.reverse());
    return gelukt;
  };
  // Eén geschiedenisstap tegelijk; wat tijdens het wachten door een nieuwe wijziging is
  // vervallen, komt niet meer op de andere stapel (anders herhaalt ⌘⇧Z iets ouds).
  const metGeschiedenis = async (fn) => {
    if (geschiedenis.current.bezig) { toast('Even wachten, de vorige stap loopt nog', { duration: 1200 }); return; }
    geschiedenis.current.bezig = true;
    try { await fn(geschiedenis.current.gen); } finally { geschiedenis.current.bezig = false; }
  };
  const ongedaan = () => metGeschiedenis(async (gen) => {
    const stap = await speelTerug(undoRef, 'undo', gen);
    if (!stap.length) return;
    if (geschiedenis.current.gen === gen) redoRef.current.push(...stap.reverse());
    toast.success(stap.length === 1 ? `Ongedaan gemaakt: ${stap[0].naam}` : `${stap.length} cellen ongedaan gemaakt`, { duration: 1200 });
  });
  const opnieuw = () => metGeschiedenis(async (gen) => {
    const stap = await speelTerug(redoRef, 'redo', gen);
    if (!stap.length) return;
    if (geschiedenis.current.gen === gen) undoRef.current.push(...stap.reverse());
    toast.success(stap.length === 1 ? `Opnieuw: ${stap[0].naam}` : `${stap.length} cellen opnieuw`, { duration: 1200 });
  });
  const toetsRef = useRef(null);
  toetsRef.current = (e) => {
    if (!(e.metaKey || e.ctrlKey)) return;
    const k = e.key.toLowerCase();
    if (k !== 'z' && k !== 'y') return;
    const a = document.activeElement;
    // In een tekstveld doet de browser zijn eigen undo; alleen buiten het typen grijpen we in.
    if (a && (a.classList?.contains('dsg-input') || ((a.tagName === 'INPUT' || a.tagName === 'TEXTAREA' || a.isContentEditable) && !a.closest('.dsg-container')))) return;
    e.preventDefault();
    if (k === 'y' || (k === 'z' && e.shiftKey)) opnieuw(); else ongedaan();
  };
  useEffect(() => {
    const h = (e) => toetsRef.current?.(e);
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, []);

  const zichtbareProjecten = useMemo(() => {
    if (projectIds) {
      const perId = new Map(projecten.map((p) => [p.id, p]));
      return projectIds.map((id) => perId.get(id)).filter(Boolean);
    }
    const s = zoek.trim().toLowerCase();
    return projecten.filter((p) => {
      if (filters.verbergOffertes && ['Offerte', 'Lead'].includes(p.status)) return false;
      if (filters.pl && !p.pl.toLowerCase().includes(filters.pl.toLowerCase())) return false;
      if (!s) return true;
      return `${p.nummer} ${p.naam} ${p.klant} ${p.pl}`.toLowerCase().includes(s);
    });
  }, [projecten, zoek, filters.pl, filters.verbergOffertes, projectIds]);

  // Stoplichten laden voor wat er in beeld staat (kleine debounce bij tikken).
  useEffect(() => {
    if (!zichtbareProjecten.length) return undefined;
    const timer = setTimeout(() => laadStoplichten(zichtbareProjecten.map((p) => p.id)), 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zichtbareProjecten]);

  const assignees = useMemo(() => {
    const set = new Set();
    Object.values(perProject).forEach((d) => (d?.taken || []).forEach((t) => {
      veldStr(t.assignee).split(',').map((x) => x.trim()).filter(Boolean).forEach((x) => set.add(x));
    }));
    return [...set].sort();
  }, [perProject]);
  // Losse namen, geen duo-combinaties: 'Joanne, Evie' als PL-paar hoort onder
  // beide namen te vallen (de filter matcht al op includes).
  const pls = useMemo(() => [...new Set(
    projecten.flatMap((p) => String(p.pl || '').split(',').map((x) => x.trim()).filter(Boolean))
  )].sort(), [projecten]);

  return (
    <Box sx={ingebed ? {} : { maxWidth: 1800, mx: 'auto', px: { xs: 2, md: 3 }, py: 2 }}>
      {/* Planningsgrid staat als tab in de kopbalk (PlanningTabs): hier alleen een stille regel. */}
      {!ingebed && <PageHeader
        kaal
        title="Alle taken per project en deliverable — klik een cel om te bewerken · ⌘C/⌘V kopiëren en plakken · ⌘D of het kopieer-icoon dupliceert een regel · ⌘⌫ of het prullenbak-icoon verwijdert · ⌘Z ongedaan"
      />}

      <Stack direction="row" spacing={1.5} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }} alignItems="center">
        {!ingebed && <><TextField
          size="small" placeholder="Zoek project…" value={zoek}
          onChange={(e) => setZoek(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18 }} /></InputAdornment> }}
          sx={{ width: 240 }}
        />
        <Select size="small" displayEmpty value={filters.pl}
          onChange={(e) => setFilters((f) => ({ ...f, pl: e.target.value }))} sx={{ minWidth: 150 }}>
          <MenuItem value="">Alle PL's</MenuItem>
          {pls.map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
        </Select>
        <Select size="small" displayEmpty value={filters.assignee}
          onChange={(e) => setFilters((f) => ({ ...f, assignee: e.target.value }))} sx={{ minWidth: 160 }}>
          <MenuItem value="">Alle assignees</MenuItem>
          {assignees.map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
        </Select>
        <Select size="small" displayEmpty value={filters.prio}
          onChange={(e) => setFilters((f) => ({ ...f, prio: e.target.value }))} sx={{ minWidth: 130 }}>
          <MenuItem value="">Alle prio's</MenuItem>
          {['Prio 1', 'Prio 2', 'Prio 3', 'Prio 4', 'Deadline', 'Draaidag', 'Geen prio', 'Wacht op feedback'].map((p) => (
            <MenuItem key={p} value={p}>{p}</MenuItem>
          ))}
        </Select>
        </>}
        <FormControlLabel
          control={<Checkbox size="small" checked={filters.verbergAfgerond}
            onChange={(e) => setFilters((f) => ({ ...f, verbergAfgerond: e.target.checked }))} />}
          label={<Typography sx={{ fontSize: 13 }}>Verberg afgerond</Typography>}
        />
        {!ingebed && <FormControlLabel
          control={<Checkbox size="small" checked={filters.verbergOffertes}
            onChange={(e) => setFilters((f) => ({ ...f, verbergOffertes: e.target.checked }))} />}
          label={<Typography sx={{ fontSize: 13 }}>Verberg offertes</Typography>}
        />}
        <Chip
          size="small"
          disabled={bulkLaden}
          icon={bulkLaden ? <CircularProgress size={13} /> : (zichtbareProjecten.length && zichtbareProjecten.every((p) => open.has(p.id)))
            ? <UnfoldLessIcon sx={{ fontSize: 15 }} /> : <UnfoldMoreIcon sx={{ fontSize: 15 }} />}
          label={(zichtbareProjecten.length && zichtbareProjecten.every((p) => open.has(p.id))) ? 'Alles inklappen' : 'Alles uitklappen'}
          onClick={async () => {
            const allesOpen = zichtbareProjecten.length && zichtbareProjecten.every((p) => open.has(p.id));
            if (allesOpen) { setOpen(new Set()); return; }
            if (zichtbareProjecten.length > 40 && !(await bevestig({ titel: `${zichtbareProjecten.length} projecten uitklappen?`, tekst: 'Dat laadt veel data — filteren op PL of zoekterm maakt het sneller.', knop: 'Uitklappen' }))) return;
            klapAllesUit(zichtbareProjecten);
          }}
          sx={{ fontWeight: 700 }}
        />
        {alleGroepKeys.length ? (
          <Chip
            size="small"
            icon={allesDicht ? <UnfoldMoreIcon sx={{ fontSize: 15 }} /> : <UnfoldLessIcon sx={{ fontSize: 15 }} />}
            label={allesDicht ? 'Deliverables uitklappen' : 'Deliverables inklappen'}
            onClick={() => setDichteGroepen(allesDicht ? new Set() : new Set(alleGroepKeys))}
            sx={{ fontWeight: 700 }}
          />
        ) : null}
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontSize: 12.5, color: '#94a3b8' }}>{zichtbareProjecten.length} projecten</Typography>
      </Stack>

      {laadFout ? <Alert severity="error" sx={{ mb: 2 }}>{laadFout}</Alert> : null}
      {bezig ? (
        <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress /></Box>
      ) : (
        zichtbareProjecten.map((p) => (
          <ProjectBlok
            key={p.id} project={p} stoplicht={stoplichten[p.id]}
            kopExtra={kopExtra ? kopExtra(p) : null} deliverableSignalen={deliverableSignalen}
            open={open.has(p.id)} onToggle={() => toggle(p.id)}
            data={perProject[p.id]} laden={ladend.has(p.id)}
            filters={filters}
            dichteGroepen={dichteGroepen}
            onToggleGroep={toggleGroep}
            onBewerk={bewerkTaak}
            onNieuweRijen={nieuweRijen}
            onVerwijderTaak={magVerwijderen ? verwijderTaak : undefined}
            onDupliceerDeliverable={dupliceerDeliverable}
            onDeliverableDeadline={wijzigDeliverableDeadline}
            onOpenDeliverable={openDeliverable}
            onNieuweTaak={(project, groep) => setTaakDlg({
              projectId: project.id,
              deliverableId: groep && groep.typeRang === null ? groep.id : null,
              titel: groep ? groep.titel : `${project.nummer}. ${project.naam}`,
            })}
            onPlanning={(project, groep, groepen) => setPlanningDlg({
              projectId: project.id,
              project,
              titel: groep.titel,
              deliverableId: String(groep.id || '').startsWith('rec') ? groep.id : null,
              taken: groep.taken,
              andereGroepen: groepen.filter((g) => g.id !== groep.id && g.typeRang === null),
            })}
            onOpenProject={(recId) => window.open(projectLinkOverzicht(recId, ingebed), '_blank', 'noopener')}
          />
        ))
      )}

      {deliverableDlg?.deliverable && (
        <DeliverableDetailDialog open={!!deliverableDlg?.deliverable} deliverable={deliverableDlg?.deliverable || { id: '', fields: {} }}
          onClose={() => setDeliverableDlg(null)}
          onUpdate={() => { const pid = deliverableDlg?.projectId; setDeliverableDlg(null); if (pid) laadTaken(pid); onGewijzigd?.(); }} />
      )}
      {taakDlg && (
        <NieuweTaakDialog info={taakDlg} onClose={() => setTaakDlg(null)}
          onKlaar={() => { const pid = taakDlg.projectId; setTaakDlg(null); laadTaken(pid); }} />
      )}

      <PlanningDialog info={planningDlg} onClose={() => setPlanningDlg(null)}
        onToepassen={pasPlanningToe}
        onHerlaad={planningDlg ? () => laadTaken(planningDlg.projectId) : undefined}
        onMail={planningDlg ? () => setMailDlg({
          project: { id: planningDlg.projectId, fields: planningDlg.project?.origFields || {} },
          groepen: [
            { id: 'huidig', titel: planningDlg.titel, taken: planningDlg.taken,
              deadline: (planningDlg.taken.find((t) => t.deadlineDeliverable) || {}).deadlineDeliverable || null },
            ...planningDlg.andereGroepen.map((g) => ({
              id: g.id, titel: g.titel, taken: g.taken,
              deadline: g.deadline || (g.taken.find((t) => t.deadlineDeliverable) || {}).deadlineDeliverable || null,
            })),
          ],
          defaultGeselecteerd: ['huidig'],
        }) : undefined} />

      {mailDlg && (
        <PlanningEmailDialog open onClose={() => setMailDlg(null)}
          project={mailDlg.project} groepen={mailDlg.groepen}
          defaultGeselecteerd={mailDlg.defaultGeselecteerd}
          msalInstance={msalInstance} account={msalAccounts?.[0]}
          senderName={msalAccounts?.[0]?.name || ''} />
      )}
    </Box>
  );
}
